import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  MessageCircle, Settings, Send, BarChart3, Shield,
  RefreshCw, Search, CheckCheck,
  XCircle, AlertTriangle, FileText, Phone, Plus, UserSearch,
  Loader2, Link2, Unplug, Zap, Calendar, Gauge, CalendarClock,
  Users, BookOpen, User, CheckCircle, X, Hash, Eye, ArrowLeft, Star, Bot, Pencil,
  Workflow, Contact, Activity,
  ChevronDown, ChevronLeft, MoreVertical, BadgeCheck, Info, Save,
  Receipt, Signal, Wifi, BatteryFull,
  UserPlus, GraduationCap, ClipboardList, AlarmClock, ClipboardCheck, BellRing, Inbox,
} from "lucide-react";
import {
  PageHeader, AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent,
  StatsCard, StatsGrid, ProgressBar, StatusBadge, EmptyState, AppBadge, Separator,
  Tabs, TabsList, TabsTrigger, TabsContent, ConfirmModal, ViewToggle, Callout, WhatsAppIcon,
} from "../ui";
import { Form, Spinner } from "react-bootstrap";
import CommonToaster from "../Common/CommonToaster";
import Shimmer from "../Common/Shimmer";
import CreateTemplateForm from "./CreateTemplateForm";
import SendMessageToStudent from "./SendMessageToStudent";
import WhatsAppChatInbox from "./WhatsAppChatInbox";
import WhatsAppSendingStatus from "./WhatsAppSendingStatus";
import ScheduledMessagesTab from "./ScheduledMessagesTab";
import ContactGroupsTab from "./ContactGroupsTab";
import BroadcastRunsTab from "./BroadcastRunsTab";
import AIAgentTab from "./AIAgentTab";
import KnowledgeBaseTab from "./KnowledgeBaseTab";
import FlowsTab from "./FlowsTab";
import WhatsAppConnectGuide from "./WhatsAppConnectGuide";
import useWhatsAppConnection from "./useWhatsAppConnection";
import { useSocket } from "../../contexts/SocketContext";
import { useBranding } from "../../contexts/BrandingContext";
import apiClient from "../../utils/apiClient";
import { usePlan } from "../../contexts/PlanContext";
import { useNavigate } from "react-router-dom";
import { hasAnyPermission } from "../../utils/permissions";
import { can } from "../../utils/access";
import { startMetaBusinessConnect } from "../../utils/metaBusinessConnect";
import { loadFacebookSdk, onEmbeddedSignupEvent } from "../../utils/facebookSdk";
import { STUDENT_VAR_GROUPS, dynamicVarLabel } from "./whatsappTemplateVars";
import { ENQUIRY_VAR_GROUPS } from "../Enquiry/enquiryWhatsappVars";
import AutomationEventPanel from "./AutomationEventPanel";
import "./whatsapp-dashboard.scss";

/* ── helpers ───────────────────────────────────────────────────────────────── */
const Loader = () => (
  <div className="wa-loader"><Loader2 size={20} className="wa-spin" /> Loading…</div>
);

/** <optgroup>s for the student field catalog, shared by the broadcast composer
 *  and the class-started automation mapping. Grouped because a flat list of ~50
 *  fields is unscannable. Single source: ./whatsappTemplateVars, which mirrors
 *  vc_be/utils/whatsappTemplateVars. */
const renderVarOptions = () => STUDENT_VAR_GROUPS.map(({ group, vars }) => (
  <optgroup key={group} label={group}>
    {vars.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
  </optgroup>
));

/** The lead's catalog (Enquiry/enquiryWhatsappVars, mirroring ENQUIRY_VARS in
 *  vc_be) — for an automation whose recipient is an enquiry, not a student. */
const renderEnquiryVarOptions = () => ENQUIRY_VAR_GROUPS.map(({ group, vars }) => (
  <optgroup key={group} label={group}>
    {vars.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
  </optgroup>
));

const statusToVariant = (s) => {
  const map = { open: "active", assigned: "pending", resolved: "completed", closed: "inactive" };
  return map[s] || "outline";
};

const tplStatusToVariant = (s) => {
  const map = { APPROVED: "active", PENDING: "pending", REJECTED: "destructive", PAUSED: "inactive", DISABLED: "inactive" };
  return map[s] || "outline";
};

const TAB_LABELS = {
  inbox: { icon: MessageCircle, label: 'Inbox' },
  analytics: { icon: BarChart3, label: 'Analytics' },
  send: { icon: UserSearch, label: 'Send Message' },
  templates: { icon: FileText, label: 'Templates' },
  broadcast: { icon: Send, label: 'Broadcast' },
  contacts: { icon: Contact, label: 'Contacts' },
  scheduled: { icon: CalendarClock, label: 'Scheduled' },
  runs: { icon: Activity, label: 'Broadcasts' },
  automation: { icon: Zap, label: 'Automation' },
  flows: { icon: Workflow, label: 'Flows' },
  aiagent: { icon: Bot, label: 'AI Agent' },
  knowledge: { icon: BookOpen, label: 'Knowledge' },
  config: { icon: Settings, label: 'Config' },
  optin: { icon: Shield, label: 'Opt-In' },
};

/* Every tab, in tab-bar order. The hash on reload and the mobile menu both read
   this, so a tab added above cannot be missing from either. */
const TAB_ORDER = Object.keys(TAB_LABELS);

/**
 * Tabs that read from or send through the WhatsApp number. Until one is
 * connected they explain that and point at the setup guide, rather than
 * rendering an empty list or a failed request that reads as a bug.
 *
 * The rest — contacts, the AI agent, its knowledge base, opt-ins — are ours, not
 * Meta's, and can be prepared before the number exists.
 */
const NEEDS_CONNECTION = {
  inbox: "Conversations with students and parents appear here once your WhatsApp number is connected.",
  analytics: "Delivery and read rates appear here once messages are going out from your number.",
  send: "Messages are sent from your institute's WhatsApp number, so it has to be connected first.",
  templates: "Templates are created and approved on your WhatsApp Business Account, so it has to be connected first.",
  broadcast: "Broadcasts are sent from your institute's WhatsApp number, so it has to be connected first.",
  scheduled: "Scheduled messages are sent from your institute's WhatsApp number, so it has to be connected first.",
  runs: "Broadcast runs appear here once your WhatsApp number is connected and sending.",
  automation: "Automatic reminders are sent from your institute's WhatsApp number and use its approved templates.",
  flows: "Flows are published to your WhatsApp Business Account, so it has to be connected first.",
};

const ConnectFirst = ({ message, canManage, onSetup }) => (
  <EmptyState
    icon={<WhatsAppIcon size={36} />}
    title="Connect WhatsApp first"
    description={message}
    action={(
      <AppButton onClick={onSetup}>
        <Link2 size={16} /> {canManage ? "Set up WhatsApp" : "See what's needed"}
      </AppButton>
    )}
  />
);

/* ══════════════════════════════════════════════════════════════════════════════
   WhatsApp Dashboard
   ══════════════════════════════════════════════════════════════════════════════ */
const WhatsAppDashboard = () => {
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const toastRef = useRef(null);
  toastRef.current = (message, type = "success") => setToast({ show: true, message, type });

  const showToast = useCallback((msg, type) => toastRef.current(msg, type), []);
  const { unreadWhatsAppCount, clearUnreadWhatsApp } = useSocket();

  // Send Message and Broadcast are nothing but a composer, so a person who may
  // not send does not get the tab at all.
  const canSendOne = can("whatsapp.reply");
  const canBroadcast = can("whatsapp.manage");
  const tabOrder = useMemo(
    () => TAB_ORDER.filter(tab => (tab !== 'send' || canSendOne) && (tab !== 'broadcast' || canBroadcast)),
    [canSendOne, canBroadcast],
  );

  /* ── Persist active tab across reloads via URL hash ──────────────────────── */
  const getInitialTab = useCallback(() => {
    const hash = window.location.hash.replace('#', '');
    return tabOrder.includes(hash) ? hash : 'inbox';
  }, [tabOrder]);
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  const handleTabChange = useCallback((value) => {
    setActiveTab(value);
    setMobileDropdownOpen(false);
    window.history.replaceState(null, '', `#${value}`);
    if (value === 'inbox') clearUnreadWhatsApp();
  }, [clearUnreadWhatsApp]);

  // Clear unread count when landing directly on inbox
  useEffect(() => {
    if (activeTab === 'inbox') clearUnreadWhatsApp();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Connection: decides between the inbox and the setup guide ────────────── */
  const connection = useWhatsAppConnection();
  const { connected, canManage, loading: connectionLoading, error: connectionError, refresh: refreshConnection } = connection;

  // An unconnected institute lands on the setup guide, not on an empty inbox.
  // Decided once, when the answer first arrives: after that the tab is the
  // user's choice. A failed check decides nothing and leaves them where they are.
  const landingDecided = useRef(false);
  useEffect(() => {
    if (landingDecided.current) return;
    if (connected === null) {
      if (connectionError) landingDecided.current = true;
      return;
    }
    landingDecided.current = true;
    if (connected === false && activeTab === 'inbox') handleTabChange('config');
  }, [connected, connectionError, activeTab, handleTabChange]);

  // The redirect dialog finishes in a Facebook pop-up (or another tab) this page
  // never hears from, so re-ask whenever the owner comes back to it. Only while
  // unconnected — it is one indexed read, but there is nothing to learn otherwise.
  const lastRecheck = useRef(0);
  useEffect(() => {
    if (connected !== false) return undefined;
    const recheck = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRecheck.current < 3000) return;
      lastRecheck.current = now;
      refreshConnection({ silent: true }).then((next) => {
        if (next?.connected) showToast("WhatsApp is connected.");
      });
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [connected, refreshConnection, showToast]);

  const goToSetup = useCallback(() => handleTabChange('config'), [handleTabChange]);

  /** A tab that needs the number, until there is one. Fails open on an error. */
  const gated = (tab, content) => {
    if (!NEEDS_CONNECTION[tab]) return content;
    if (connected === null && connectionLoading) return <Shimmer height={56} count={3} />;
    if (connected === false) {
      return <ConnectFirst message={NEEDS_CONNECTION[tab]} canManage={canManage} onSetup={goToSetup} />;
    }
    return content;
  };

  // ── Mobile: render tab selector in top nav portal ────────────────────────
  const portalTarget = document.getElementById('top-nav-portal');
  const ActiveIcon = TAB_LABELS[activeTab]?.icon || MessageCircle;

  useEffect(() => {
    // Close dropdown on outside click
    if (!mobileDropdownOpen) return;
    const handler = (e) => {
      if (!e.target.closest('.wa-mobile-tab-selector')) setMobileDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mobileDropdownOpen]);

  // Cleanup portal content on unmount
  useEffect(() => {
    return () => setMobileDropdownOpen(false);
  }, []);

  return (
    <div className="wa-dashboard">
      {/* Portal: mobile tab selector in top nav bar */}
      {portalTarget && createPortal(
        <div className="wa-mobile-tab-selector">
          <button
            type="button"
            className={`wa-mobile-tab-btn ${mobileDropdownOpen ? 'open' : ''}`}
            onClick={() => setMobileDropdownOpen(prev => !prev)}
          >
            <ActiveIcon size={14} />
            <span>{TAB_LABELS[activeTab]?.label || 'Inbox'}</span>
            {unreadWhatsAppCount > 0 && activeTab !== 'inbox' && (
              <span className="wa-tab-badge">{unreadWhatsAppCount > 99 ? '99+' : unreadWhatsAppCount}</span>
            )}
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className={`wa-mobile-chevron ${mobileDropdownOpen ? 'open' : ''}`}>
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {mobileDropdownOpen && (
            <div className="wa-mobile-tab-menu">
              {tabOrder.map(tab => {
                const TabIcon = TAB_LABELS[tab]?.icon || MessageCircle;
                return (
                  <button
                    key={tab}
                    type="button"
                    className={`wa-mobile-tab-item ${tab === activeTab ? 'active' : ''}`}
                    onClick={() => handleTabChange(tab)}
                  >
                    <TabIcon size={14} />
                    <span>{TAB_LABELS[tab]?.label}</span>
                    {tab === 'inbox' && unreadWhatsAppCount > 0 && (
                      <span className="wa-tab-badge">{unreadWhatsAppCount > 99 ? '99+' : unreadWhatsAppCount}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>,
        portalTarget
      )}

      <PageHeader
        title="WhatsApp"
        description="Manage WhatsApp notifications, conversations, and templates"
        icon={MessageCircle}
      />

      <Tabs value={activeTab} onChange={handleTabChange} className="wa-tabs">
        <TabsList overflow>
          <TabsTrigger value="inbox">
            <MessageCircle size={14} /> Inbox
            {unreadWhatsAppCount > 0 && activeTab !== 'inbox' && (
              <span className="wa-tab-badge">{unreadWhatsAppCount > 99 ? '99+' : unreadWhatsAppCount}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="analytics"><BarChart3 size={14} /> Analytics</TabsTrigger>
          {canSendOne && <TabsTrigger value="send"><UserSearch size={14} /> Send Message</TabsTrigger>}
          <TabsTrigger value="templates"><FileText size={14} /> Templates</TabsTrigger>
          {canBroadcast && <TabsTrigger value="broadcast"><Send size={14} /> Broadcast</TabsTrigger>}
          <TabsTrigger value="contacts"><Contact size={14} /> Contacts</TabsTrigger>
          <TabsTrigger value="scheduled"><CalendarClock size={14} /> Scheduled</TabsTrigger>
          <TabsTrigger value="runs"><Activity size={14} /> Broadcasts</TabsTrigger>
          <TabsTrigger value="automation"><Zap size={14} /> Automation</TabsTrigger>
          <TabsTrigger value="flows"><Workflow size={14} /> Flows</TabsTrigger>
          <TabsTrigger value="aiagent"><Bot size={14} /> AI Agent</TabsTrigger>
          <TabsTrigger value="knowledge"><BookOpen size={14} /> Knowledge</TabsTrigger>
          <TabsTrigger value="config"><Settings size={14} /> Config</TabsTrigger>
          <TabsTrigger value="optin"><Shield size={14} /> Opt-In</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox">{gated('inbox', <InboxTab showToast={showToast} />)}</TabsContent>
        <TabsContent value="analytics">{gated('analytics', <AnalyticsTab showToast={showToast} />)}</TabsContent>
        {canSendOne && <TabsContent value="send">{gated('send', <SendMessageToStudent showToast={showToast} />)}</TabsContent>}
        <TabsContent value="templates">{gated('templates', <TemplatesTab showToast={showToast} />)}</TabsContent>
        {canBroadcast && <TabsContent value="broadcast">{gated('broadcast', <BroadcastTab showToast={showToast} />)}</TabsContent>}
        <TabsContent value="contacts"><ContactGroupsTab showToast={showToast} /></TabsContent>
        <TabsContent value="scheduled">{gated('scheduled', <ScheduledMessagesTab showToast={showToast} />)}</TabsContent>
        <TabsContent value="runs">{gated('runs', <BroadcastRunsTab showToast={showToast} />)}</TabsContent>
        <TabsContent value="automation">{gated('automation', <AutomationTab showToast={showToast} />)}</TabsContent>
        <TabsContent value="flows">{gated('flows', <FlowsTab showToast={showToast} />)}</TabsContent>
        <TabsContent value="aiagent"><AIAgentTab showToast={showToast} /></TabsContent>
        <TabsContent value="knowledge"><KnowledgeBaseTab showToast={showToast} /></TabsContent>
        <TabsContent value="config"><ConfigTab showToast={showToast} connection={connection} /></TabsContent>
        <TabsContent value="optin"><OptInTab showToast={showToast} /></TabsContent>
      </Tabs>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

/* ─── Analytics Tab ────────────────────────────────────────────────────────── */
const AnalyticsTab = ({ showToast }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await apiClient.get(`/whatsapp/analytics?days=${days}`);
      setData(d);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to load analytics", "error");
    }
    setLoading(false);
  }, [days, showToast]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader />;

  if (!data) {
    return (
      <EmptyState
        icon={<BarChart3 size={32} />}
        title="No Analytics Data"
        description="Configure WhatsApp in the Config tab first to start collecting analytics."
      />
    );
  }

  const ov = data.overview;
  const convStats = data.conversationStats || {};
  const tplAnalytics = data.templateAnalytics || {};

  return (
    <div className="wa-analytics">
      {/* Controls */}
      <div className="wa-analytics__controls">
        <div className="settings-field" style={{ maxWidth: 160 }}>
          <select value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        <AppButton variant="outline" size="sm" onClick={load}>
          <RefreshCw size={13} /> Refresh
        </AppButton>
      </div>

      {/* KPI Cards */}
      <StatsGrid cols={5} className="wa-stats-grid">
        <StatsCard title="Messages Sent" value={ov?.totals?.outbound || 0} icon={Send} iconColor="blue" />
        <StatsCard title="Delivered" value={ov?.outboundStatuses?.delivered || 0} icon={CheckCheck} iconColor="emerald" />
        <StatsCard title="Read" value={ov?.outboundStatuses?.read || 0} icon={CheckCheck} iconColor="violet" />
        <StatsCard title="Failed" value={ov?.outboundStatuses?.failed || 0} icon={XCircle} iconColor="rose" />
        <StatsCard title="Inbound" value={ov?.totals?.inbound || 0} icon={MessageCircle} iconColor="amber" />
      </StatsGrid>

      {/* Rates */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><BarChart3 size={18} /> Delivery Rates</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="wa-rates-grid">
            {[
              { label: "Delivery Rate", pct: ov?.rates?.deliveryRate || 0, color: "emerald" },
              { label: "Read Rate", pct: ov?.rates?.readRate || 0, color: "blue" },
              { label: "Fail Rate", pct: ov?.rates?.failRate || 0, color: "rose" },
            ].map(r => (
              <div key={r.label} className="wa-rate-item">
                <div className="wa-rate-item__header">
                  <span>{r.label}</span>
                  <strong>{r.pct}%</strong>
                </div>
                <ProgressBar value={r.pct} color={r.color} size="sm" />
              </div>
            ))}
          </div>
        </AppCardContent>
      </AppCard>

      {/* Conversation Stats */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><MessageCircle size={18} /> Conversations</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="wa-conv-stats">
            {["open", "assigned", "resolved", "closed"].map(s => (
              <div key={s} className="wa-conv-stat-item">
                <span className="wa-conv-stat-item__value">{convStats[s] || 0}</span>
                <StatusBadge status={statusToVariant(s)}>{s}</StatusBadge>
              </div>
            ))}
          </div>
        </AppCardContent>
      </AppCard>

      {/* Template Usage */}
      {Object.keys(tplAnalytics).length > 0 && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle><FileText size={18} /> Template Performance</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <div className="wa-table-wrap">
              <table className="wa-table">
                <thead>
                  <tr>
                    <th>Template</th><th>Total</th><th>Delivered</th><th>Read</th><th>Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(tplAnalytics).map(([name, s]) => (
                    <tr key={name}>
                      <td className="wa-table__mono">{name}</td>
                      <td>{s.total}</td>
                      <td className="wa-table__emerald">{s.delivered || 0}</td>
                      <td className="wa-table__blue">{s.read || 0}</td>
                      <td className="wa-table__rose">{s.failed || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AppCardContent>
        </AppCard>
      )}
    </div>
  );
};

/* ─── Inbox Tab (uses shared WhatsApp Chat Inbox) ──────────────────────────── */
const InboxTab = ({ showToast }) => {
  const { socket, refreshUnreadWhatsApp } = useSocket();
  const navigate = useNavigate();
  const { hasPlanFeature } = usePlan();
  // Converting a chat is adding an enquiry — the same plan and right as the
  // Enquiries page's Add. Opening a linked one needs only the right to view.
  const enquiriesOn = hasPlanFeature("enquiryManagement");
  const canConvert = enquiriesOn && can("enquiries.fromWhatsapp");
  const canOpenEnquiry = enquiriesOn && hasAnyPermission("view_enquiries", "add_edit_enquiries");
  const canReply = can("whatsapp.reply");
  // Page 1 is the most recently active slice; higher pages are older chats the
  // inbox appends as the admin scrolls the list. `search` goes to the server —
  // filtering here would only ever search the page already loaded, which is how
  // any contact older than one page became unreachable.
  const fetchConversations = useCallback(async (statusFilter, { page = 1, limit = 30, search = "" } = {}) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (statusFilter) params.set("status", statusFilter);
    if (search) params.set("search", search);
    const { data } = await apiClient.get(`/whatsapp/conversations?${params.toString()}`);
    return {
      conversations: (data.conversations || []).map(c => ({
        _id: c._id,
        name: c.customerName || c.studentId?.name || "",
        phone: c.customerPhone,
        preview: c.lastMessagePreview,
        updatedAt: c.updatedAt,
        unreadCount: c.unreadCount,
        status: c.status,
        studentName: c.studentId?.name,
        enquiryId: c.enquiryId?._id || null,
        enquiryName: c.enquiryId?.fullName || "",
        enquiryStatus: c.enquiryId?.status || "",
        aiPaused: !!c.aiPaused,
        aiHandoffReason: c.aiHandoffReason,
        aiTurnCount: c.aiTurnCount,
      })),
      hasMore: (data.page || page) < (data.pages || 0),
    };
  }, []);

  // Page 1 is the newest slice; higher pages are older history pulled in as the
  // admin scrolls up. This API returns newest-first, so each page is reversed
  // into the oldest-first order the inbox renders.
  const fetchMessages = useCallback(async (convId, { page = 1, limit = 50 } = {}) => {
    const { data } = await apiClient.get(
      `/whatsapp/conversations/${convId}/messages?page=${page}&limit=${limit}`);
    return {
      messages: (data.messages || []).reverse(),
      hasMore: (data.page || page) < (data.pages || 0),
    };
  }, []);

  const sendReply = useCallback(async (conversation, payload) => {
    // Backwards-compat: a plain string still works.
    const body = typeof payload === "string" ? { messageType: "text", text: payload } : payload;
    await apiClient.post(`/whatsapp/conversations/${conversation._id}/reply`, body);
  }, []);

  const updateStatus = useCallback(async (convId, status) => {
    await apiClient.patch(`/whatsapp/conversations/${convId}/status`, { status });
  }, []);

  const deleteMessage = useCallback(async (message) => {
    await apiClient.delete(`/whatsapp/messages/${message._id}`);
  }, []);

  // Approved templates for the inbox picker (re-open expired windows / new numbers).
  const fetchTemplates = useCallback(async () => {
    const { data } = await apiClient.get("/whatsapp/templates");
    return data.templates || [];
  }, []);

  // Catalogue of actions a button can be wired to. Server-defined — the client
  // never invents an action name, it only picks one the backend already knows.
  const fetchButtonActions = useCallback(async () => {
    const { data } = await apiClient.get("/whatsapp/actions");
    return data.actions || [];
  }, []);

  // Tokens are minted server-side from this payload, so the browser never sees
  // or supplies a capability — it names an action and the backend binds it.
  const sendInteractiveButtons = useCallback(async (conversation, payload) => {
    await apiClient.post(`/whatsapp/conversations/${conversation._id}/interactive`, payload);
  }, []);

  // Meta's live verdict on whether this number can deliver. The server caches
  // it for a few minutes, so opening the inbox does not spend three Graph calls
  // per page view; `force` is the Re-check button asking past that.
  const fetchSendingHealth = useCallback(async (force) => {
    const { data } = await apiClient.get(`/whatsapp/health${force ? "?refresh=1" : ""}`);
    return data;
  }, []);

  // A chat → an enquiry on the chat's own number. A 409 (the number already
  // has one) reaches the modal as the axios error, which asks what to do.
  const convertToEnquiry = useCallback(async (conversation, body) => {
    const { data } = await apiClient.post(`/enquiries/from-whatsapp/${conversation._id}`, body);
    return data;
  }, []);

  const loadEnquiryCourses = useCallback(async () => {
    const { data } = await apiClient.get("/courses/list");
    return data.courses || [];
  }, []);

  const openEnquiry = useCallback((enquiryId) => navigate(`/enquiries/${enquiryId}`), [navigate]);

  return (
    <WhatsAppChatInbox
      fetchConversations={fetchConversations}
      fetchMessages={fetchMessages}
      sendReply={sendReply}
      updateStatus={updateStatus}
      deleteMessage={canReply ? deleteMessage : undefined}
      onConversationRead={refreshUnreadWhatsApp}
      showToast={showToast}
      socket={socket}
      fetchTemplates={fetchTemplates}
      fetchButtonActions={fetchButtonActions}
      sendInteractiveButtons={sendInteractiveButtons}
      fetchSendingHealth={fetchSendingHealth}
      convertToEnquiry={canConvert ? convertToEnquiry : undefined}
      loadEnquiryCourses={canConvert ? loadEnquiryCourses : undefined}
      openEnquiry={canOpenEnquiry ? openEnquiry : undefined}
      canReply={canReply}
    />
  );
};

/* ─── Templates Tab ────────────────────────────────────────────────────────── */
/* ── Template Detail Viewer ────────────────────────────────────────────────── */
/**
 * TemplateButtonActions — wires each QUICK_REPLY button of an approved template
 * to a server-side action.
 *
 * Only quick-reply buttons appear: a URL button already navigates and a phone
 * button already dials, so neither can carry a payload back to us. The binding
 * is institute-local config — the template itself stays exactly as Meta
 * approved it, and a resync will not disturb this.
 */
const TemplateButtonActions = ({ template, showToast }) => {
  const canManage = can("whatsapp.manage");
  const [actions, setActions] = useState([]);
  const [bindings, setBindings] = useState({});   // index → { action, targetId }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const quickReplies = useMemo(() => {
    const comp = (template.components || []).find(c => (c.type || '').toUpperCase() === 'BUTTONS');
    return (comp?.buttons || [])
      .map((b, i) => ({ ...b, index: i }))
      .filter(b => (b.type || '').toUpperCase() === 'QUICK_REPLY');
  }, [template]);

  useEffect(() => {
    let active = true;
    apiClient.get('/whatsapp/actions')
      .then(({ data }) => { if (active) setActions(data.actions || []); })
      .catch(() => { if (active) setActions([]); })
      .finally(() => { if (active) setLoading(false); });
    const existing = {};
    (template.buttonActions || []).forEach(b => {
      existing[b.index] = { action: b.action, targetId: b.targetId || '' };
    });
    setBindings(existing);
    return () => { active = false; };
  }, [template]);

  const actionByKey = useMemo(
    () => Object.fromEntries(actions.map(a => [a.key, a])), [actions],
  );

  const setBinding = (index, field, value) => {
    setBindings(prev => {
      const next = { ...prev, [index]: { ...prev[index], [field]: value } };
      if (field === 'action') {
        if (!value) delete next[index];
        else if (!actionByKey[value]?.targetModel) next[index].targetId = '';
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(bindings)
        .filter(([, v]) => v?.action)
        .map(([index, v]) => ({
          index: Number(index),
          action: v.action,
          ...(v.targetId ? { targetId: v.targetId } : {}),
        }));
      await apiClient.put(`/whatsapp/templates/${template._id}/button-actions`, { buttonActions: payload });
      showToast?.('Button actions saved');
    } catch (err) {
      showToast?.(err?.response?.data?.message || 'Failed to save button actions', 'error');
    }
    setSaving(false);
  };

  /* Rendering nothing here reads as "the feature is missing" rather than "it
     doesn't apply to this template" — which is exactly how it was first
     reported. Say why instead, and say what to do about it. */
  if (!quickReplies.length) {
    return (
      <div className="wa-tpl-detail__comp">
        <div className="wa-tpl-detail__comp-type">BUTTON ACTIONS</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary, #9ca3af)', lineHeight: 1.6 }}>
          This template has no quick-reply buttons, so there is nothing to wire up.
          Only quick-reply buttons can trigger an action — a website button navigates
          away and a call button dials, so neither sends anything back to us.
          <br />
          To add one, create a template and choose <strong>Add button → Custom</strong>.
        </div>
      </div>
    );
  }

  return (
    <div className="wa-tpl-detail__comp">
      <div className="wa-tpl-detail__comp-type">BUTTON ACTIONS</div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary, #9ca3af)', marginBottom: 10 }}>
        Choose what happens when each quick-reply button is tapped. The action runs
        on the server, bound to the person the message was sent to.
      </div>

      {loading ? (
        <div style={{ fontSize: '0.78rem' }}>Loading actions…</div>
      ) : (
        <>
          {quickReplies.map(btn => {
            const bound = bindings[btn.index] || {};
            const def = actionByKey[bound.action];
            return (
              <div key={btn.index} className="wa-tpl-detail__btn-row" style={{ flexWrap: 'wrap', gap: 8 }}>
                <AppBadge variant="secondary" size="sm">{btn.text}</AppBadge>
                <Form.Select
                  size="sm"
                  style={{ maxWidth: 260 }}
                  value={bound.action || ''}
                  onChange={e => setBinding(btn.index, 'action', e.target.value)}
                  disabled={!canManage}
                >
                  <option value="">— no action (reply only) —</option>
                  {actions.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                </Form.Select>
                {def?.targetModel && (
                  <Form.Control
                    size="sm"
                    style={{ maxWidth: 220 }}
                    value={bound.targetId || ''}
                    onChange={e => setBinding(btn.index, 'targetId', e.target.value)}
                    placeholder={`${def.targetModel} ID (optional)`}
                    disabled={!canManage}
                  />
                )}
                {def?.principal === 'user' && (
                  <span style={{ fontSize: '0.68rem', color: '#92400e' }}>
                    Admin only — recipient must be a {(def.roles || []).join(' or ')}
                  </span>
                )}
              </div>
            );
          })}
          {canManage && (
            <div style={{ marginTop: 10 }}>
              <AppButton size="sm" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save button actions'}
              </AppButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * WhatsApp-style bubble rendering of a template's components.
 *
 * Shared by the Recommended grid and the institute's own Templates grid: a
 * template slug ("fee_reminder_v2") tells the customer nothing about what the
 * message actually says, so every list that names a template shows its text.
 *
 * Component types are matched case-insensitively — Meta's sync returns them
 * uppercase, but `createTemplate` lowercases them for the create call and a
 * locally-submitted template holds whatever the form sent until the next sync.
 */
const TemplateBubble = ({ components }) => {
  const find = (type) => components?.find(c => (c.type || '').toUpperCase() === type);
  const header = find('HEADER');
  const body = find('BODY');
  const footer = find('FOOTER');
  const btns = find('BUTTONS');
  const format = (header?.format || 'TEXT').toUpperCase();
  const mediaLabel = { IMAGE: '📷 Image Header', VIDEO: '🎥 Video Header', DOCUMENT: '📄 Document Header', LOCATION: '📍 Location Header' }[format];
  const isEmpty = !body?.text && !footer?.text && !btns?.buttons?.length && !mediaLabel && !header?.text;

  return (
    <div className="wa-rec-card__bubble">
      {format === 'TEXT' && header?.text && (
        <div className="wa-rec-card__header">{header.text}</div>
      )}
      {mediaLabel && <div className="wa-rec-card__media">{mediaLabel}</div>}
      {body?.text && <div className="wa-rec-card__body">{body.text}</div>}
      {footer?.text && <div className="wa-rec-card__footer">{footer.text}</div>}
      {btns?.buttons?.length > 0 && (
        <div className="wa-rec-card__buttons">
          {btns.buttons.map((btn, i) => {
            const type = (btn.type || '').toUpperCase();
            const icon = { URL: '🔗', PHONE_NUMBER: '📞', COPY_CODE: '📋', FLOW: '⚡' }[type] || '↩️';
            // COPY_CODE carries no label — Meta renders the code itself, and the
            // example arrives as a bare string or a one-element array.
            const example = Array.isArray(btn.example) ? btn.example[0] : btn.example;
            return (
              <div key={i} className="wa-rec-card__btn">
                {icon} {btn.text || (type === 'COPY_CODE' ? example : '') || type}
              </div>
            );
          })}
        </div>
      )}
      {/* An approved template with no readable component (some AUTHENTICATION
          templates carry none) would otherwise render as a blank green box. */}
      {isEmpty && <div className="wa-rec-card__empty">No preview available — sync from Meta to fetch the message text.</div>}
    </div>
  );
};

const TemplateDetailView = ({ template, onBack, showToast }) => {
  if (!template) return null;
  const header = template.components?.find(c => c.type === 'HEADER');
  const body = template.components?.find(c => c.type === 'BODY');
  const footer = template.components?.find(c => c.type === 'FOOTER');
  const buttons = template.components?.find(c => c.type === 'BUTTONS');

  return (
    <AppCard>
      <AppCardHeader>
        <div className="wa-card-header-row">
          <AppCardTitle>
            <AppButton variant="ghost" size="sm" onClick={onBack} title="Back to list">
              <ArrowLeft size={16} />
            </AppButton>
            <FileText size={18} /> {template.name}
          </AppCardTitle>
          <div className="wa-inline-actions">
            <AppBadge variant="secondary" size="sm">{template.language}</AppBadge>
            <AppBadge variant="secondary" size="sm">{template.category || '—'}</AppBadge>
            <StatusBadge status={tplStatusToVariant(template.status)}>{template.status}</StatusBadge>
          </div>
        </div>
      </AppCardHeader>
      <AppCardContent>
        <div className="wa-tpl-detail">
          {/* WhatsApp-style bubble preview */}
          <div className="wa-tpl-detail__preview">
            <div className="wa-tpl-detail__preview-label">Message Preview</div>
            <div className="wa-bc-preview__bubble">
              {header?.text && <div className="wa-bc-preview__header">{header.text}</div>}
              {header?.format === 'IMAGE' && <div className="wa-tpl-detail__media-tag">📷 Image Header</div>}
              {header?.format === 'VIDEO' && <div className="wa-tpl-detail__media-tag">🎥 Video Header</div>}
              {header?.format === 'DOCUMENT' && <div className="wa-tpl-detail__media-tag">📄 Document Header</div>}
              {body?.text && <div className="wa-bc-preview__body">{body.text}</div>}
              {footer?.text && <div className="wa-bc-preview__footer">{footer.text}</div>}
              {buttons?.buttons?.length > 0 && (
                <div className="wa-bc-preview__buttons">
                  {buttons.buttons.map((btn, i) => (
                    <div key={i} className="wa-bc-preview__btn">
                      {btn.type === 'URL' ? '🔗' : btn.type === 'PHONE_NUMBER' ? '📞' : '↩️'} {btn.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Raw components breakdown */}
          <div className="wa-tpl-detail__components">
            <div className="wa-tpl-detail__preview-label">Components</div>
            {header && (
              <div className="wa-tpl-detail__comp">
                <div className="wa-tpl-detail__comp-type">HEADER ({header.format || 'TEXT'})</div>
                <div className="wa-tpl-detail__comp-text">{header.text || `[${header.format} media]`}</div>
              </div>
            )}
            {body && (
              <div className="wa-tpl-detail__comp">
                <div className="wa-tpl-detail__comp-type">BODY</div>
                <div className="wa-tpl-detail__comp-text">{body.text}</div>
              </div>
            )}
            {footer && (
              <div className="wa-tpl-detail__comp">
                <div className="wa-tpl-detail__comp-type">FOOTER</div>
                <div className="wa-tpl-detail__comp-text">{footer.text}</div>
              </div>
            )}
            {buttons?.buttons?.length > 0 && (
              <div className="wa-tpl-detail__comp">
                <div className="wa-tpl-detail__comp-type">BUTTONS</div>
                {buttons.buttons.map((btn, i) => (
                  <div key={i} className="wa-tpl-detail__btn-row">
                    <AppBadge variant="secondary" size="sm">{btn.type}</AppBadge>
                    <span>{btn.text}</span>
                    {btn.url && <span className="wa-tpl-detail__btn-url">{btn.url}</span>}
                    {btn.phone_number && <span className="wa-tpl-detail__btn-url">{btn.phone_number}</span>}
                  </div>
                ))}
              </div>
            )}
            {template.parameterCount > 0 && (
              <div className="wa-tpl-detail__comp">
                <div className="wa-tpl-detail__comp-type">PARAMETERS</div>
                <div className="wa-tpl-detail__comp-text">{template.parameterCount} variable(s) expected</div>
              </div>
            )}
            <TemplateButtonActions template={template} showToast={showToast} />
          </div>
        </div>
      </AppCardContent>
    </AppCard>
  );
};

const TemplatesTab = ({ showToast }) => {
  const canManage = can("whatsapp.manage");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteTemplateName, setDeleteTemplateName] = useState(null);
  const [viewTemplate, setViewTemplate] = useState(null);
  const [showRecommended, setShowRecommended] = useState(false);
  const [recommended, setRecommended] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const [usingTemplate, setUsingTemplate] = useState(null);
  const [customizing, setCustomizing] = useState(null); // recommended tpl being edited
  // Cards by default — the slug alone doesn't tell the customer what the message
  // says. The table stays one click away for scanning a long list.
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/whatsapp/templates");
      setTemplates(data.templates || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Search covers the message text too, not just the slug — the whole point of
  // the preview is that customers think of a template by what it says.
  const visibleTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t => {
      const body = t.components?.find(c => (c.type || "").toUpperCase() === "BODY");
      return `${t.name} ${t.category || ""} ${body?.text || ""}`.toLowerCase().includes(q);
    });
  }, [templates, search]);

  const syncTemplates = async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post("/whatsapp/templates/sync");
      showToast(data.message || "Templates synced");
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Sync failed", "error");
    }
    setSyncing(false);
  };

  const handleCreate = async (formData) => {
    const { data } = await apiClient.post("/whatsapp/templates/create", formData);
    showToast(data.message || "Template submitted");
    setShowCreate(false);
    load();
  };

  const handleDelete = (name) => setDeleteTemplateName(name);

  const confirmDelete = async () => {
    const name = deleteTemplateName;
    setDeleteTemplateName(null);
    setDeleting(name);
    try {
      await apiClient.delete(`/whatsapp/templates/${encodeURIComponent(name)}`);
      showToast("Template deleted");
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Delete failed", "error");
    }
    setDeleting(null);
  };

  const loadRecommended = async () => {
    setShowRecommended(true);
    setLoadingRecommended(true);
    try {
      const { data } = await apiClient.get("/whatsapp/recommended-templates");
      setRecommended(data.templates || []);
    } catch { /* ignore */ }
    setLoadingRecommended(false);
  };

  const adoptRecommended = async (tpl) => {
    setUsingTemplate(tpl._id);
    try {
      const { data } = await apiClient.post("/whatsapp/templates/use-recommended", { templateId: tpl._id });
      showToast(data.message || "Template submitted for approval");
      setShowRecommended(false);
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to use template", "error");
    }
    setUsingTemplate(null);
  };

  const confirmModal = (
    <ConfirmModal
      show={!!deleteTemplateName}
      onHide={() => setDeleteTemplateName(null)}
      onConfirm={confirmDelete}
      title="Delete Template"
      message={`Delete template "${deleteTemplateName}"? This will also delete it from Meta.`}
      confirmText="Delete"
      variant="danger"
    />
  );

  if (viewTemplate) {
    return <><TemplateDetailView template={viewTemplate} onBack={() => setViewTemplate(null)} showToast={showToast} />{confirmModal}</>;
  }

  // Customize-and-submit a recommended template (pre-filled editor flow).
  if (customizing) {
    const handleCustomizeSubmit = async (formData) => {
      // Goes through the standard create endpoint — submits the (possibly edited)
      // template to Meta on the institute's own WABA.
      const { data } = await apiClient.post("/whatsapp/templates/create", formData);
      showToast(data.message || "Template submitted");
      setCustomizing(null);
      setShowRecommended(false);
      load();
    };
    return (
      <>
        {/* overflowVisible: the form's "Add button" dropdown is absolutely
            positioned and would otherwise be clipped by the card. */}
        <AppCard overflowVisible>
          <AppCardHeader>
            <div className="wa-card-header-row">
              <AppCardTitle><Pencil size={18} /> Customize: {customizing.name}</AppCardTitle>
              <AppButton size="sm" variant="ghost" onClick={() => setCustomizing(null)}>
                <ArrowLeft size={13} /> Back
              </AppButton>
            </div>
          </AppCardHeader>
          <AppCardContent>
            <CreateTemplateForm
              initialValues={customizing}
              onSubmit={handleCustomizeSubmit}
              onCancel={() => setCustomizing(null)}
              submitLabel="Submit customized template"
            />
          </AppCardContent>
        </AppCard>
        {confirmModal}
      </>
    );
  }

  if (showRecommended) {
    return (
      <>
        <AppCard>
          <AppCardHeader>
            <div className="wa-card-header-row">
              <AppCardTitle><Star size={18} /> Recommended Templates</AppCardTitle>
              <AppButton size="sm" variant="ghost" onClick={() => setShowRecommended(false)}>
                <ArrowLeft size={13} /> Back to Templates
              </AppButton>
            </div>
          </AppCardHeader>
          <AppCardContent>
            {loadingRecommended ? <Loader /> : recommended.length === 0 ? (
              <EmptyState
                icon={<Star size={32} />}
                title="No Recommended Templates"
                description="No recommended templates are available yet. Your platform admin will add them soon."
              />
            ) : (
              <div className="wa-rec-grid">
                {recommended.map(t => {
                  const alreadyExists = templates.some(
                    et => et.name === t.name && et.language === t.language
                  );
                  return (
                    <div className="wa-rec-card" key={t._id}>
                      <TemplateBubble components={t.components} />
                      <div className="wa-rec-card__info">
                        <span className="wa-rec-card__name">{t.name}</span>
                        <div className="wa-rec-card__meta">
                          <AppBadge variant="secondary" size="sm">{t.category || "—"}</AppBadge>
                          <AppBadge variant="secondary" size="sm">{t.language}</AppBadge>
                          {t.parameterCount > 0 && (
                            <AppBadge variant="outline" size="sm">{t.parameterCount} param{t.parameterCount > 1 ? "s" : ""}</AppBadge>
                          )}
                        </div>
                      </div>
                      <div className="wa-rec-card__action">
                        {alreadyExists ? (
                          <AppBadge variant="outline" size="sm">Already added</AppBadge>
                        ) : canManage && (
                          <>
                            <AppButton
                              size="sm"
                              variant="outline"
                              onClick={() => setCustomizing(t)}
                              className="wa-rec-card__use-btn"
                            >
                              <Pencil size={13} /> Customize
                            </AppButton>
                            <AppButton
                              size="sm"
                              onClick={() => adoptRecommended(t)}
                              disabled={usingTemplate === t._id}
                              className="wa-rec-card__use-btn"
                            >
                              {usingTemplate === t._id ? (
                                <><Loader2 size={13} className="wa-spin" /> Submitting...</>
                              ) : (
                                <><Plus size={13} /> Use as-is</>
                              )}
                            </AppButton>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </AppCardContent>
        </AppCard>
        {confirmModal}
      </>
    );
  }

  if (showCreate) {
    return (
      <>
        {/* overflowVisible — see the Customize card above. */}
        <AppCard overflowVisible>
          <AppCardHeader>
            <AppCardTitle><FileText size={18} /> Create Template</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <CreateTemplateForm onSubmit={handleCreate} onCancel={() => setShowCreate(false)} />
          </AppCardContent>
        </AppCard>
        {confirmModal}
      </>
    );
  }

  return (
    <>
    <AppCard>
      <AppCardHeader>
        <div className="wa-card-header-row">
          <AppCardTitle><FileText size={18} /> Templates</AppCardTitle>
          <div className="wa-inline-actions">
            <span className="wa-text-muted wa-text-sm">
              {search.trim()
                ? `${visibleTemplates.length} of ${templates.length} template(s)`
                : `${templates.length} template(s)`}
            </span>
            <AppButton size="sm" variant="outline" onClick={loadRecommended}>
              <Star size={13} /> Recommended
            </AppButton>
            {canManage && (
              <>
                <AppButton size="sm" onClick={() => setShowCreate(true)}>
                  <Plus size={13} /> Create Template
                </AppButton>
                <AppButton size="sm" onClick={syncTemplates} disabled={syncing}>
                  <RefreshCw size={13} className={syncing ? "wa-spin" : ""} />
                  {syncing ? "Syncing..." : "Sync from Meta"}
                </AppButton>
              </>
            )}
          </div>
        </div>
      </AppCardHeader>
      <AppCardContent>
        {loading ? <Loader /> : templates.length === 0 ? (
          <EmptyState
            icon={<FileText size={32} />}
            title="No Templates"
            description='Click "Sync from Meta" to fetch your approved templates, or create a new one.'
          />
        ) : (
          <>
            <div className="wa-tpl-toolbar">
              <div className="wa-tpl-toolbar__search">
                <Search size={15} />
                <input
                  type="text"
                  placeholder="Search by name or message text…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <button type="button" onClick={() => setSearch("")} title="Clear">
                    <X size={14} />
                  </button>
                )}
              </div>
              <ViewToggle view={view} onViewChange={setView} />
            </div>

            {visibleTemplates.length === 0 ? (
              <EmptyState
                icon={<Search size={32} />}
                title="No matching templates"
                description={`Nothing matches "${search}". Try a different word from the message.`}
              />
            ) : view === "grid" ? (
              <div className="wa-rec-grid">
                {visibleTemplates.map(t => (
                  <div className="wa-rec-card wa-tpl-card" key={t._id}>
                    <TemplateBubble components={t.components} />
                    <div className="wa-rec-card__info">
                      <span className="wa-rec-card__name">{t.name}</span>
                      <div className="wa-rec-card__meta">
                        <StatusBadge status={tplStatusToVariant(t.status)}>{t.status}</StatusBadge>
                        <AppBadge variant="secondary" size="sm">{t.category || "—"}</AppBadge>
                        <AppBadge variant="secondary" size="sm">{t.language}</AppBadge>
                        {t.parameterCount > 0 && (
                          <AppBadge variant="outline" size="sm">{t.parameterCount} param{t.parameterCount > 1 ? "s" : ""}</AppBadge>
                        )}
                      </div>
                      {/* Meta's reason is the only thing that explains a REJECTED
                          template; it was previously stored and never shown. */}
                      {t.status === "REJECTED" && t.rejectedReason && (
                        <div className="wa-tpl-card__reject">
                          <AlertTriangle size={12} /> {t.rejectedReason}
                        </div>
                      )}
                    </div>
                    <div className="wa-rec-card__action wa-tpl-card__action">
                      <span className="wa-tpl-card__usage">Used {t.usageCount || 0}×</span>
                      <div className="wa-inline-actions">
                        <AppButton
                          variant="ghost" size="sm"
                          onClick={() => setViewTemplate(t)}
                          title="View template"
                        >
                          <Eye size={13} />
                        </AppButton>
                        {canManage && (
                          <AppButton
                            variant="ghost" size="sm"
                            onClick={() => handleDelete(t.name)}
                            disabled={deleting === t.name}
                            title="Delete template"
                          >
                            {deleting === t.name ? <Loader2 size={13} className="wa-spin" /> : <XCircle size={13} />}
                          </AppButton>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="wa-table-wrap">
                <table className="wa-table">
                  <thead>
                    <tr><th>Name</th><th>Message</th><th>Language</th><th>Category</th><th>Status</th><th>Params</th><th>Used</th><th></th></tr>
                  </thead>
                  <tbody>
                    {visibleTemplates.map(t => {
                      const body = t.components?.find(c => (c.type || "").toUpperCase() === "BODY");
                      return (
                        <tr key={t._id}>
                          <td className="wa-table__mono">{t.name}</td>
                          {/* One-line gist; the card view and the detail view show it in full. */}
                          <td className="wa-tpl-row__msg" title={body?.text || ""}>{body?.text || "—"}</td>
                          <td>{t.language}</td>
                          <td><AppBadge variant="secondary" size="sm">{t.category || "—"}</AppBadge></td>
                          <td><StatusBadge status={tplStatusToVariant(t.status)}>{t.status}</StatusBadge></td>
                          <td>{t.parameterCount}</td>
                          <td>{t.usageCount || 0}</td>
                          <td className="wa-table__actions">
                            <AppButton
                              variant="ghost" size="sm"
                              onClick={() => setViewTemplate(t)}
                              title="View template"
                            >
                              <Eye size={13} />
                            </AppButton>
                            {canManage && (
                              <AppButton
                                variant="ghost" size="sm"
                                onClick={() => handleDelete(t.name)}
                                disabled={deleting === t.name}
                                title="Delete template"
                              >
                                {deleting === t.name ? <Loader2 size={13} className="wa-spin" /> : <XCircle size={13} />}
                              </AppButton>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </AppCardContent>
    </AppCard>
    {confirmModal}
    </>
  );
};

/* ─── Broadcast Tab ────────────────────────────────────────────────────────── */

/* ── Template variable extraction helpers (shared with SendMessageToStudent) ── */
function _extractParams(comp) {
  if (!comp?.text) return [];
  const params = [];
  const regex = /\{\{([^}]+)\}\}/g;
  let match;
  const seen = new Set();
  while ((match = regex.exec(comp.text)) !== null) {
    const raw = match[1].trim();
    if (seen.has(raw)) continue;
    seen.add(raw);
    let example = "";
    if (comp.example?.body_text_named_params) {
      const np = comp.example.body_text_named_params.find(p => p.param_name === raw);
      if (np) example = np.example || "";
    } else if (comp.example?.body_text?.[0]) {
      const idx = parseInt(raw, 10);
      if (!isNaN(idx) && idx >= 1) example = comp.example.body_text[0][idx - 1] || "";
    } else if (comp.example?.header_text) {
      const idx = parseInt(raw, 10);
      if (!isNaN(idx) && idx >= 1) example = comp.example.header_text[idx - 1] || "";
    }
    const isPositional = /^\d+$/.test(raw);
    // `token` mirrors `key` here, but button params below namespace their key —
    // see _extractBtnParams for why everything Meta-facing reads `token`.
    params.push({ key: raw, token: raw, label: isPositional ? `Parameter ${raw}` : raw.replace(/_/g, " "), example });
  }
  return params;
}

function _extractBtnParams(comp) {
  if (comp?.type !== "BUTTONS" || !comp.buttons) return [];
  const params = [];
  comp.buttons.forEach((btn, i) => {
    if (btn.type === "URL" && btn.url) {
      const regex = /\{\{(\d+)\}\}/g;
      let match;
      // Extract the static base URL before the {{1}} placeholder
      const baseUrl = btn.url.replace(/\{\{\d+\}\}/g, "").replace(/\/$/, "");
      while ((match = regex.exec(btn.url)) !== null) {
        const raw = match[1].trim();
        let example = btn.example?.[0] || "";
        // `key` is namespaced so two buttons' {{1}} don't collide in state;
        // `token` is what Meta sees. A URL-button param is always positional, so
        // sending the namespaced key as parameter_name gives Meta a named param
        // it cannot place.
        params.push({ key: `btn_${i}_${raw}`, token: raw, label: `Button "${btn.text}" URL suffix`, example, buttonIndex: i, baseUrl });
      }
    }
  });
  return params;
}

function _extractTemplateVars(template) {
  if (!template?.components) return { header: [], body: [], button: [] };
  const hc = template.components.find(c => c.type === "HEADER");
  const bc = template.components.find(c => c.type === "BODY");
  const btc = template.components.find(c => c.type === "BUTTONS");
  // IMAGE/VIDEO/DOCUMENT headers take a media link per send, not text params.
  const hdrFormat = (hc?.format || "").toUpperCase();
  const mediaHeader = ["IMAGE", "VIDEO", "DOCUMENT"].includes(hdrFormat) ? hdrFormat : "";
  return {
    header: _extractParams(hc),
    body: _extractParams(bc),
    button: _extractBtnParams(btc),
    mediaHeader,
  };
}

function _buildComponents(vars, values) {
  const components = [];
  const mkParam = (p, prefix) => {
    const param = { type: "text", text: values[`${prefix}_${p.key}`] || "" };
    // Off `token`, not `key`: a namespaced button key would go out as a named
    // parameter, and Meta cannot place a named param on a positional slot.
    if (!/^\d+$/.test(p.token)) param.parameter_name = p.token;
    return param;
  };
  // A media header replaces the text header component entirely — the two never
  // coexist, and omitting it makes Meta reject every message in the broadcast.
  if (vars.mediaHeader) {
    const kind = vars.mediaHeader.toLowerCase();
    components.push({
      type: "header",
      parameters: [{ type: kind, [kind]: { link: (values.header_media || "").trim() } }],
    });
  } else if (vars.header.length) components.push({ type: "header", parameters: vars.header.map(p => mkParam(p, "header")) });
  if (vars.body.length) components.push({ type: "body", parameters: vars.body.map(p => mkParam(p, "body")) });
  if (vars.button.length) {
    const byBtn = {};
    vars.button.forEach(p => { if (!byBtn[p.buttonIndex]) byBtn[p.buttonIndex] = []; byBtn[p.buttonIndex].push(p); });
    Object.entries(byBtn).forEach(([idx, params]) => {
      components.push({ type: "button", sub_type: "url", index: Number(idx), parameters: params.map(p => mkParam(p, "button")) });
    });
  }
  return components;
}

function _fillPreview(text, params, values, prefix) {
  if (!text) return text;
  let result = text;
  // Match on `token` (what is actually in the text) but look the value up by
  // `key` (what the state map is keyed on).
  params.forEach(p => { result = result.replace(`{{${p.token}}}`, values[`${prefix}_${p.key}`] || `{{${p.token}}}`); });
  return result;
}

const AUDIENCE_TYPES = [
  { value: "all", label: "All Students", icon: Users, desc: "Every active student in your institute" },
  { value: "contact_groups", label: "Contact Groups", icon: Contact, desc: "Your saved lists of phone numbers" },
  { value: "course", label: "By Course", icon: BookOpen, desc: "All students enrolled in a specific course" },
  { value: "batch", label: "By Batch", icon: Hash, desc: "All students in a specific batch" },
  { value: "students", label: "Specific Students", icon: UserSearch, desc: "Search and select individual students" },
  { value: "external_numbers", label: "External Numbers", icon: Phone, desc: "Enter phone numbers manually (not in LMS)" },
];

/**
 * Above this many recipients the send goes to `POST /whatsapp/runs` (paced,
 * cancellable, notifies on completion) instead of `POST /whatsapp/broadcast`
 * (synchronous, hard-capped at 1,000 server-side).
 *
 * Both paths are kept rather than routing everything through runs: a 40-person
 * batch reminder should return "queued 40" while the admin is still looking at
 * the screen, not become a background job they have to go and check on. The
 * threshold sits below the server's 1,000 cap so the old endpoint is never the
 * one that rejects.
 */
const RUN_THRESHOLD = 500;

/** "2h 15m" — a large send runs for hours, so minutes alone stop being useful. */
function formatSendDuration(minutes) {
  if (!minutes || minutes < 1) return "a minute";
  if (minutes < 60) return `${minutes} minutes`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h} hour${h !== 1 ? "s" : ""}`;
}

const BroadcastTab = ({ showToast }) => {
  // ── Data loading ──
  const [templates, setTemplates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Audience ──
  const [audienceType, setAudienceType] = useState("all");
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [studentStatus, setStudentStatus] = useState("active");
  const [courseId, setCourseId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentQuery, setStudentQuery] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [studentSearching, setStudentSearching] = useState(false);
  const searchTimer = useRef(null);

  // ── External numbers ──
  const [externalNumbersText, setExternalNumbersText] = useState("");
  const parsedExternalNumbers = useMemo(() => {
    if (!externalNumbersText.trim()) return [];
    return externalNumbersText
      .split(/[\n,;]+/)
      .map(n => n.trim().replace(/[\s()+\-]/g, ''))
      .filter(n => n.length >= 10);
  }, [externalNumbersText]);

  // ── Template ──
  const [templateName, setTemplateName] = useState("");
  const [paramValues, setParamValues] = useState({});
  // paramModes tracks each param as 'dynamic' or 'static'
  // key format: "header_1", "body_name", "button_btn_0_1"
  const [paramModes, setParamModes] = useState({});

  // ── Recipient count + send capacity ──
  const [recipientCount, setRecipientCount] = useState(null);
  // What the institute's daily cap and the worker's throughput mean for this
  // audience — surfaced before sending, not discovered afterwards.
  const [capacity, setCapacity] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const countTimer = useRef(null);

  // ── Send ──
  const [confirmBroadcast, setConfirmBroadcast] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // ── Derived ──
  const selectedTemplate = useMemo(() => templates.find(t => t.name === templateName) || null, [templates, templateName]);
  const templateVars = useMemo(() => selectedTemplate ? _extractTemplateVars(selectedTemplate) : { header: [], body: [], button: [], mediaHeader: "" }, [selectedTemplate]);
  const totalParams = templateVars.header.length + templateVars.body.length + templateVars.button.length;

  const filteredBatches = useMemo(() => {
    if (audienceType === "batch" && courseId) {
      return batches.filter(b => (b.course?._id || b.course) === courseId);
    }
    return batches;
  }, [batches, audienceType, courseId]);

  const textParamsFilled = totalParams === 0 || [
    ...templateVars.header.map(p => `header_${p.key}`),
    ...templateVars.body.map(p => `body_${p.key}`),
    ...templateVars.button.map(p => `button_${p.key}`),
  ].every(k => {
    // Dynamic params are always "filled" (resolved per-student on backend)
    if (paramModes[k] === 'dynamic' && paramValues[k]) return true;
    // Static params need a non-empty value
    return paramModes[k] !== 'dynamic' && paramValues[k]?.trim();
  });
  // A media header is required but is not one of the counted text params, so it
  // gates the send separately.
  const allParamsFilled = textParamsFilled
    && (!templateVars.mediaHeader || !!paramValues.header_media?.trim());

  const audienceReady = (
    (audienceType === "all") ||
    (audienceType === "course" && courseId) ||
    (audienceType === "batch" && batchId) ||
    (audienceType === "students" && selectedStudents.length > 0) ||
    (audienceType === "external_numbers" && parsedExternalNumbers.length > 0) ||
    (audienceType === "contact_groups" && selectedGroupIds.length > 0)
  );

  /**
   * Contact groups always take the run path, whatever their size — a saved
   * audience is the one that grows between sends, and a group that quietly
   * crosses 1,000 would otherwise start failing on the old endpoint's cap.
   */
  const useRunPath = audienceType === "contact_groups" || (recipientCount ?? 0) > RUN_THRESHOLD;

  const canSend = templateName && allParamsFilled && audienceReady;

  // ── Load initial data ──
  useEffect(() => {
    (async () => {
      try {
        const [tplRes, optRes, grpRes] = await Promise.all([
          apiClient.get("/whatsapp/templates"),
          apiClient.get("/whatsapp/broadcast/options"),
          // Fail-soft on its own: a student broadcast must stay possible when
          // the contacts endpoint is unavailable.
          apiClient.get("/whatsapp/contact-groups").catch(() => ({ data: { groups: [] } })),
        ]);
        setTemplates((tplRes.data.templates || []).filter(t => t.status === "APPROVED"));
        setCourses(optRes.data.courses || []);
        setBatches(optRes.data.batches || []);
        setContactGroups(grpRes.data.groups || []);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  // ── Reset params when template changes ──
  useEffect(() => { setParamValues({}); setParamModes({}); }, [templateName]);

  // ── Reset state when audience or template changes ──
  useEffect(() => { setConfirmBroadcast(false); setShowConfirmModal(false); setSendResult(null); }, [audienceType, courseId, batchId, selectedStudents.length, parsedExternalNumbers.length, selectedGroupIds.length, templateName, studentStatus]);

  // ── Fetch recipient count when audience changes ──
  useEffect(() => {
    if (countTimer.current) clearTimeout(countTimer.current);
    setRecipientCount(null);
    setCapacity(null);

    if (!audienceReady) return;

    countTimer.current = setTimeout(async () => {
      setCountLoading(true);
      try {
        const body = { audienceType, recipientStatus: studentStatus, studentStatus };
        if (audienceType === "course") body.courseId = courseId;
        if (audienceType === "batch") body.batchId = batchId;
        if (audienceType === "students") body.studentIds = selectedStudents.map(s => s._id);
        if (audienceType === "contact_groups") body.contactGroupIds = selectedGroupIds;
        // External numbers now count server-side rather than locally: the server
        // normalises to E.164 and dedupes, so "9876543210" and "+91 98765 43210"
        // are one recipient. Counting them here as two showed a total the send
        // then never matched.
        if (audienceType === "external_numbers") body.externalNumbers = parsedExternalNumbers;
        // /runs/estimate returns the count AND what the daily cap and worker
        // throughput mean for it, which /broadcast/count does not.
        const { data } = await apiClient.post("/whatsapp/runs/estimate", body);
        setRecipientCount(data.count);
        setCapacity(data.capacity || null);
      } catch {
        setRecipientCount(null);
        setCapacity(null);
      }
      setCountLoading(false);
    }, 300);

    return () => { if (countTimer.current) clearTimeout(countTimer.current); };
  }, [audienceType, courseId, batchId, selectedStudents, audienceReady, studentStatus, parsedExternalNumbers, selectedGroupIds]);

  // ── Student search ──
  const handleStudentSearch = useCallback((value) => {
    setStudentQuery(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.trim().length < 2) { setStudentResults([]); setStudentSearching(false); return; }
    setStudentSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/whatsapp/students/search?q=${encodeURIComponent(value.trim())}`);
        setStudentResults(data.students || []);
      } catch { setStudentResults([]); }
      setStudentSearching(false);
    }, 350);
  }, []);

  const addStudent = (s) => {
    if (!selectedStudents.find(x => x._id === s._id)) {
      setSelectedStudents(prev => [...prev, s]);
    }
    setStudentQuery("");
    setStudentResults([]);
  };

  const removeStudent = (id) => {
    setSelectedStudents(prev => prev.filter(s => s._id !== id));
  };

  // ── Send broadcast ──
  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setSendResult(null);
    try {
      const payload = {
        templateName,
        languageCode: selectedTemplate?.language || "en",
        audienceType,
        studentStatus,
        confirmBroadcast: true,
      };

      // Build paramConfig for per-student personalization
      if (totalParams > 0) {
        const buildEntries = (params, prefix) => params.map(p => {
          const k = `${prefix}_${p.key}`;
          const mode = paramModes[k] || "static";
          return {
            // `token`, not `key` — the backend decides positional vs named from
            // this, and a namespaced button key reads as a named parameter.
            key: p.token,
            type: mode,
            variable: mode === "dynamic" ? (paramValues[k] || "") : "",
            value: mode === "static" ? (paramValues[k] || "") : "",
            ...(p.buttonIndex !== undefined ? { buttonIndex: p.buttonIndex } : {}),
          };
        });

        payload.paramConfig = {
          header: buildEntries(templateVars.header, "header"),
          body: buildEntries(templateVars.body, "body"),
          button: buildEntries(templateVars.button, "button"),
        };

        // Also send legacy components for backward compat (static-only fallback)
        payload.components = _buildComponents(templateVars, paramValues);
      }

      if (audienceType === "course") payload.courseId = courseId;
      if (audienceType === "batch") payload.batchId = batchId;
      if (audienceType === "students") payload.studentIds = selectedStudents.map(s => s._id);
      if (audienceType === "external_numbers") payload.externalNumbers = parsedExternalNumbers;
      if (audienceType === "contact_groups") payload.contactGroupIds = selectedGroupIds;

      if (useRunPath) {
        // Background run: returns 202 with a runId, not a sent count. Progress
        // and the completion notification come from the Broadcasts tab.
        payload.title = `Broadcast — ${templateName}`;
        const { data } = await apiClient.post("/whatsapp/runs", payload);
        showToast(data.message || "Broadcast started");
        setSendResult({ success: true, queued: data.totalRecipients, runId: data.runId, background: true });
      } else {
        const { data } = await apiClient.post("/whatsapp/broadcast", payload);
        showToast(data.message || "Broadcast queued");
        setSendResult({ success: true, queued: data.queued });
      }
      setConfirmBroadcast(false);
      setShowConfirmModal(false);
    } catch (err) {
      showToast(err?.response?.data?.message || "Broadcast failed", "error");
      setSendResult({ success: false, message: err?.response?.data?.message || "Failed" });
    }
    setSending(false);
  };

  if (loading) return <Loader />;

  return (
    <div className="wa-broadcast">
      {/* ── Step 1: Audience Selection ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Users size={18} /> Step 1 — Select Audience</AppCardTitle>
          <p className="settings-card-desc">Choose who will receive this broadcast message</p>
        </AppCardHeader>
        <AppCardContent>
          <div className="wa-bc-audience-grid">
            {AUDIENCE_TYPES.map(a => {
              const Icon = a.icon;
              return (
                <div
                  key={a.value}
                  className={`wa-bc-audience-card ${audienceType === a.value ? "wa-bc-audience-card--active" : ""}`}
                  onClick={() => { setAudienceType(a.value); setCourseId(""); setBatchId(""); setSelectedStudents([]); setExternalNumbersText(""); setSelectedGroupIds([]); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === "Enter" && setAudienceType(a.value)}
                >
                  <div className="wa-bc-audience-card__icon"><Icon size={20} /></div>
                  <div className="wa-bc-audience-card__label">{a.label}</div>
                  <div className="wa-bc-audience-card__desc">{a.desc}</div>
                </div>
              );
            })}
          </div>

          {/* Contact Group multi-select */}
          {audienceType === "contact_groups" && (
            <div className="wa-bc-filter-section">
              <Form.Label>Select one or more groups</Form.Label>
              {contactGroups.length === 0 ? (
                <div className="wa-alert wa-alert--info">
                  <AlertTriangle size={14} />
                  No contact groups yet. Create one in the <strong>Contacts</strong> tab and
                  import your numbers there once — then pick it here every time.
                </div>
              ) : (
                <>
                  <div className="wa-sched-form__group-list">
                    {contactGroups.map(g => {
                      const checked = selectedGroupIds.includes(g._id);
                      return (
                        <label
                          key={g._id}
                          className={`wa-sched-form__group ${checked ? "is-checked" : ""}`}
                          style={{ "--cg-color": g.color || "#25D366" }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => setSelectedGroupIds(prev => (
                              checked ? prev.filter(id => id !== g._id) : [...prev, g._id]
                            ))}
                          />
                          <span className="wa-sched-form__group-dot" />
                          <span className="wa-sched-form__group-name">{g.name}</span>
                          <span className="wa-sched-form__group-count">
                            {(g.activeCount || 0).toLocaleString()}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {selectedGroupIds.length > 1 && (
                    <div className="wa-form-hint" style={{ marginTop: 6 }}>
                      A number in more than one selected group is messaged <strong>once</strong>.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Student Status Filter — meaningless for audiences that are not
              students: a contact carries its own active/unsubscribed state and
              unsubscribed contacts are always excluded. */}
          {audienceType !== "external_numbers" && audienceType !== "contact_groups" && (
          <div className="wa-bc-filter-section" style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12, marginBottom: 4 }}>
            <Form.Label style={{ marginBottom: 0, fontSize: "0.82rem", fontWeight: 500 }}>Send to:</Form.Label>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                className={`wa-mode-btn ${studentStatus === "active" ? "wa-mode-btn--active" : ""}`}
                onClick={() => setStudentStatus("active")}
              >
                <CheckCircle size={13} /> Active Students
              </button>
              <button
                type="button"
                className={`wa-mode-btn ${studentStatus === "all" ? "wa-mode-btn--active" : ""}`}
                onClick={() => setStudentStatus("all")}
              >
                <Users size={13} /> All Students
              </button>
            </div>
          </div>
          )}

          {/* Course Selector */}
          {audienceType === "course" && (
            <div className="wa-bc-filter-section">
              <Form.Group>
                <Form.Label>Select Course</Form.Label>
                <Form.Select value={courseId} onChange={e => setCourseId(e.target.value)}>
                  <option value="">Choose a course...</option>
                  {courses.map(c => <option key={c._id} value={c._id}>{c.name}{c.title ? ` (${c.title})` : ""}</option>)}
                </Form.Select>
              </Form.Group>
            </div>
          )}

          {/* Batch Selector */}
          {audienceType === "batch" && (
            <div className="wa-bc-filter-section">
              <Form.Group className="mb-2">
                <Form.Label>Filter by Course (optional)</Form.Label>
                <Form.Select value={courseId} onChange={e => { setCourseId(e.target.value); setBatchId(""); }}>
                  <option value="">All Courses</option>
                  {courses.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Select Batch</Form.Label>
                <Form.Select value={batchId} onChange={e => setBatchId(e.target.value)}>
                  <option value="">Choose a batch...</option>
                  {filteredBatches.map(b => (
                    <option key={b._id} value={b._id}>
                      {b.name}{b.course?.name ? ` — ${b.course.name}` : ""}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          )}

          {/* Student Picker */}
          {audienceType === "students" && (
            <div className="wa-bc-filter-section">
              <Form.Label>Search &amp; Select Students</Form.Label>
              <div className="wa-bc-student-search-box">
                <Search size={15} className="wa-bc-student-search-icon" />
                <input
                  type="text"
                  className="wa-bc-student-search-input"
                  placeholder="Search by name or mobile number..."
                  value={studentQuery}
                  onChange={e => handleStudentSearch(e.target.value)}
                />
                {studentSearching && <Spinner animation="border" size="sm" />}
              </div>

              {studentResults.length > 0 && (
                <div className="wa-bc-student-results">
                  {studentResults.filter(s => !selectedStudents.find(x => x._id === s._id)).map(s => (
                    <div key={s._id} className="wa-bc-student-result" onClick={() => addStudent(s)} role="button" tabIndex={0} onKeyDown={e => e.key === "Enter" && addStudent(s)}>
                      <User size={14} />
                      <span className="wa-bc-student-result__name">{s.name}</span>
                      <span className="wa-bc-student-result__meta">{s.mobile}</span>
                      {s.course?.name && <span className="wa-bc-student-result__meta">{s.course.name}</span>}
                    </div>
                  ))}
                </div>
              )}

              {selectedStudents.length > 0 && (
                <div className="wa-bc-selected-students">
                  <div className="wa-bc-selected-students__label">
                    Selected ({selectedStudents.length})
                    <button className="wa-bc-clear-all" onClick={() => setSelectedStudents([])}>Clear all</button>
                  </div>
                  <div className="wa-bc-chips">
                    {selectedStudents.map(s => (
                      <div key={s._id} className="wa-bc-chip">
                        <User size={12} />
                        <span>{s.name}</span>
                        <button className="wa-bc-chip__remove" onClick={() => removeStudent(s._id)} title="Remove"><X size={12} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* External Numbers Input */}
          {audienceType === "external_numbers" && (
            <div className="wa-bc-filter-section">
              <label className="wa-form-label" style={{ marginBottom: 6 }}>Phone Numbers</label>
              <textarea
                className="wa-form-input wa-form-textarea"
                rows={5}
                value={externalNumbersText}
                onChange={e => setExternalNumbersText(e.target.value)}
                placeholder={"Enter phone numbers (one per line, or comma/semicolon separated)\n\nExamples:\n919876543210\n918765432109\n917654321098"}
              />
              <div className="wa-form-hint" style={{ marginTop: 6 }}>
                Include country code (e.g. 91 for India). Numbers not in your LMS will also receive the message.
              </div>
              {parsedExternalNumbers.length > 0 && (
                <div className="wa-form-hint" style={{ marginTop: 4 }}>
                  <strong>{parsedExternalNumbers.length}</strong> valid number{parsedExternalNumbers.length !== 1 ? "s" : ""} detected
                </div>
              )}
            </div>
          )}

          {/* Recipient Count Badge */}
          {audienceReady && (
            <div className="wa-bc-recipient-count">
              <Users size={14} />
              {countLoading ? (
                <span>Counting recipients…</span>
              ) : recipientCount !== null ? (
                <span>
                  <strong>{recipientCount.toLocaleString()}</strong>{" "}
                  {audienceType === "external_numbers" ? "phone number"
                    : audienceType === "contact_groups" ? "contact"
                      : `${studentStatus === "all" ? "" : "active "}student`}
                  {recipientCount !== 1 ? "s" : ""} will receive this broadcast
                </span>
              ) : (
                <span>Unable to estimate recipients</span>
              )}
            </div>
          )}

          {/* What this send actually costs in time, and whether the daily cap
              will swallow most of it. Placed with the audience rather than in
              the confirm dialog so it can still be changed in response. */}
          {capacity && recipientCount > 0 && (
            <>
              {useRunPath && (
                <div className="wa-alert wa-alert--info" style={{ marginTop: 8 }}>
                  <Gauge size={14} />
                  WhatsApp is rate-limited, so this sends in the background over roughly{" "}
                  <strong>{formatSendDuration(capacity.estimatedMinutes)}</strong>. You can
                  carry on using the LMS — track it in the <strong>Broadcasts</strong> tab and
                  you&rsquo;ll be notified when it finishes.
                </div>
              )}
              {capacity.willExceedDailyLimit && (
                <div className="wa-alert wa-alert--warning" style={{ marginTop: 8 }}>
                  <AlertTriangle size={14} />
                  Your daily WhatsApp limit is <strong>{capacity.dailyLimit.toLocaleString()}</strong>{" "}
                  and <strong>{capacity.dailyUsed.toLocaleString()}</strong> have been used today,
                  so about <strong>{capacity.overflowCount.toLocaleString()}</strong> of these will
                  be skipped rather than sent. Raise the limit in <strong>Config</strong> first if
                  they all need to go out.
                </div>
              )}
            </>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Step 2: Template Selection & Configuration ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><FileText size={18} /> Step 2 — Select Template &amp; Fill Variables</AppCardTitle>
          <p className="settings-card-desc">Choose an approved WhatsApp template and fill in any dynamic variables</p>
        </AppCardHeader>
        <AppCardContent>
          {templates.length === 0 ? (
            <EmptyState
              icon={<FileText size={32} />}
              title="No Approved Templates"
              description='Sync your templates from Meta in the Templates tab, or create a new one.'
            />
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Template</Form.Label>
                <Form.Select value={templateName} onChange={e => setTemplateName(e.target.value)}>
                  <option value="">Select a template...</option>
                  {templates.map(t => (
                    <option key={t._id} value={t.name}>
                      {t.name} ({t.language}) — {t.category || "MARKETING"}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Media header link — required for IMAGE/VIDEO/DOCUMENT templates.
                  The sample approved with the template is not reused; every send
                  supplies its own media. */}
              {selectedTemplate && templateVars.mediaHeader && (
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                    Header {templateVars.mediaHeader.toLowerCase()} URL
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    value={paramValues.header_media || ""}
                    onChange={e => setParamValues(pv => ({ ...pv, header_media: e.target.value }))}
                    placeholder="https://example.com/banner.jpg"
                  />
                  <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary, #9ca3af)", marginTop: 4 }}>
                    Public HTTPS link, same {templateVars.mediaHeader.toLowerCase()} for every recipient.
                  </div>
                </Form.Group>
              )}

              {/* Variable inputs */}
              {selectedTemplate && totalParams > 0 && (
                <div className="wa-bc-vars">
                  <div className="wa-bc-vars__title">Template Variables ({totalParams})</div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary, #9ca3af)", marginBottom: 10 }}>
                    For each variable, choose <strong>Dynamic</strong> (different per student, e.g., student name) or <strong>Static</strong> (same value for everyone).
                  </div>
                  {templateVars.header.length > 0 && (
                    <div className="wa-bc-vars__group">
                      <div className="wa-bc-vars__group-label">Header</div>
                      {templateVars.header.map(p => {
                        const k = `header_${p.key}`;
                        const mode = paramModes[k] || "static";
                        return (
                          <div key={k} className="mb-2">
                            <Form.Label className="wa-bc-var-label">{p.label}</Form.Label>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <div style={{ display: "flex", gap: 4 }}>
                                <button type="button" className={`wa-mode-btn ${mode === "dynamic" ? "wa-mode-btn--active" : ""}`} style={{ fontSize: "0.7rem", padding: "2px 8px" }} onClick={() => { setParamModes(m => ({ ...m, [k]: "dynamic" })); setParamValues(pv => ({ ...pv, [k]: "" })); }}>Dynamic</button>
                                <button type="button" className={`wa-mode-btn ${mode === "static" ? "wa-mode-btn--active" : ""}`} style={{ fontSize: "0.7rem", padding: "2px 8px" }} onClick={() => { setParamModes(m => ({ ...m, [k]: "static" })); setParamValues(pv => ({ ...pv, [k]: "" })); }}>Static</button>
                              </div>
                              {mode === "dynamic" ? (
                                <Form.Select size="sm" style={{ maxWidth: 220 }} value={paramValues[k] || ""} onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))}>
                                  <option value="">-- Select variable --</option>
                                  {renderVarOptions()}
                                </Form.Select>
                              ) : (
                                <Form.Control size="sm" value={paramValues[k] || ""} onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))} placeholder={p.example || `Enter ${p.label}...`} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {templateVars.body.length > 0 && (
                    <div className="wa-bc-vars__group">
                      <div className="wa-bc-vars__group-label">Body</div>
                      {templateVars.body.map(p => {
                        const k = `body_${p.key}`;
                        const mode = paramModes[k] || "static";
                        return (
                          <div key={k} className="mb-2">
                            <Form.Label className="wa-bc-var-label">{p.label}</Form.Label>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <div style={{ display: "flex", gap: 4 }}>
                                <button type="button" className={`wa-mode-btn ${mode === "dynamic" ? "wa-mode-btn--active" : ""}`} style={{ fontSize: "0.7rem", padding: "2px 8px" }} onClick={() => { setParamModes(m => ({ ...m, [k]: "dynamic" })); setParamValues(pv => ({ ...pv, [k]: "" })); }}>Dynamic</button>
                                <button type="button" className={`wa-mode-btn ${mode === "static" ? "wa-mode-btn--active" : ""}`} style={{ fontSize: "0.7rem", padding: "2px 8px" }} onClick={() => { setParamModes(m => ({ ...m, [k]: "static" })); setParamValues(pv => ({ ...pv, [k]: "" })); }}>Static</button>
                              </div>
                              {mode === "dynamic" ? (
                                <Form.Select size="sm" style={{ maxWidth: 220 }} value={paramValues[k] || ""} onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))}>
                                  <option value="">-- Select variable --</option>
                                  {renderVarOptions()}
                                </Form.Select>
                              ) : (
                                <Form.Control size="sm" value={paramValues[k] || ""} onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))} placeholder={p.example || `Enter ${p.label}...`} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {templateVars.button.length > 0 && (
                    <div className="wa-bc-vars__group">
                      <div className="wa-bc-vars__group-label">Buttons</div>
                      {templateVars.button.map(p => {
                        const k = `button_${p.key}`;
                        return (
                          <Form.Group key={k} className="mb-2">
                            <Form.Label className="wa-bc-var-label">{p.label}</Form.Label>
                            {p.baseUrl && (
                              <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary, #9ca3af)", marginBottom: 4, fontFamily: "monospace", wordBreak: "break-all" }}>
                                Base URL: <strong>{p.baseUrl}</strong>/
                                <span style={{ color: "hsl(var(--chart-5))" }}>{"{{suffix}}"}</span>
                              </div>
                            )}
                            <Form.Control
                              size="sm"
                              value={paramValues[k] || ""}
                              onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))}
                              placeholder={p.example || `Enter only the path/suffix (not full URL)...`}
                            />
                            <Form.Text className="text-muted" style={{ fontSize: "0.68rem" }}>
                              Only enter the suffix part. The base URL is fixed in your Meta template. Final link: {p.baseUrl}/{paramValues[k] || "..."}
                            </Form.Text>
                          </Form.Group>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Live Preview */}
              {selectedTemplate && (
                <BroadcastPreview template={selectedTemplate} vars={templateVars} values={paramValues} modes={paramModes} />
              )}
            </>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Step 3: Review & Send ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Send size={18} /> Step 3 — Review &amp; Send</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          {/* Summary */}
          <div className="wa-bc-summary">
            <div className="wa-bc-summary__row">
              <span className="wa-bc-summary__label">Audience</span>
              <span>{AUDIENCE_TYPES.find(a => a.value === audienceType)?.label || audienceType}
                {audienceType === "course" && courseId && ` — ${courses.find(c => c._id === courseId)?.name || ""}`}
                {audienceType === "batch" && batchId && ` — ${batches.find(b => b._id === batchId)?.name || ""}`}
                {audienceType === "students" && ` — ${selectedStudents.length} selected`}
                {audienceType === "external_numbers" && ` — ${parsedExternalNumbers.length} number(s)`}
              </span>
            </div>
            <div className="wa-bc-summary__row">
              <span className="wa-bc-summary__label">Recipients</span>
              <span>{countLoading ? "Counting…" : recipientCount !== null ? `${recipientCount} recipient(s)` : "—"}</span>
            </div>
            <div className="wa-bc-summary__row">
              <span className="wa-bc-summary__label">Template</span>
              <span>{templateName || "Not selected"}</span>
            </div>
          </div>

          {sendResult && (
            <div className={`wa-alert ${sendResult.success ? "wa-alert--success" : "wa-alert--warning"}`}>
              {sendResult.success ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
              {!sendResult.success ? sendResult.message
                : sendResult.background
                  // Deliberately not "sent": at this point nothing has been sent.
                  // The run has been created and the first page queued; the last
                  // message may be hours away.
                  ? `Broadcast started for ${(sendResult.queued || 0).toLocaleString()} recipient(s). It runs in the background — follow it in the Broadcasts tab.`
                  : `Broadcast queued for ${sendResult.queued} recipient(s)!`}
            </div>
          )}

          <Separator />
          <div className="settings-save-row">
            <AppButton onClick={() => setShowConfirmModal(true)} disabled={!canSend || sending}>
              <Send size={14} /> {useRunPath ? "Start Broadcast" : "Send Broadcast"}
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── Broadcast Confirmation Modal ── */}
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => { if (!sending) setShowConfirmModal(false); }}
        onConfirm={handleSend}
        title="Confirm Broadcast"
        variant="warning"
        confirmText="Send Broadcast"
        cancelText="Cancel"
        loading={sending}
        message={
          <div className="wa-confirm-broadcast">
            {/* Preview */}
            {selectedTemplate && (
              <BroadcastPreview template={selectedTemplate} vars={templateVars} values={paramValues} modes={paramModes} />
            )}
            {/* Summary */}
            <div className="wa-bc-summary" style={{ marginTop: 12 }}>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Audience</span>
                <span>{AUDIENCE_TYPES.find(a => a.value === audienceType)?.label || audienceType}
                  {audienceType === "course" && courseId && ` — ${courses.find(c => c._id === courseId)?.name || ""}`}
                  {audienceType === "batch" && batchId && ` — ${batches.find(b => b._id === batchId)?.name || ""}`}
                  {audienceType === "students" && ` — ${selectedStudents.length} selected`}
                  {audienceType === "external_numbers" && ` — ${parsedExternalNumbers.length} number(s)`}
                </span>
              </div>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Recipients</span>
                <span><strong>{recipientCount ?? "?"}</strong> recipient(s)</span>
              </div>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Template</span>
                <span>{templateName}</span>
              </div>
            </div>
            <div className="wa-alert wa-alert--warning" style={{ marginTop: 10, marginBottom: 0 }}>
              <AlertTriangle size={14} />
              This will send a WhatsApp message to <strong>{recipientCount ?? "?"} recipient(s)</strong>. This action cannot be undone.
            </div>
          </div>
        }
      />
    </div>
  );
};

/* ── Broadcast Template Preview ── */
const BroadcastPreview = ({ template, vars, values, modes = {} }) => {
  const hc = template.components?.find(c => c.type === "HEADER");
  const bc = template.components?.find(c => c.type === "BODY");
  const fc = template.components?.find(c => c.type === "FOOTER");
  const btc = template.components?.find(c => c.type === "BUTTONS");

  // Build preview values that show dynamic labels for dynamic params
  const previewValues = { ...values };
  Object.entries(modes).forEach(([k, mode]) => {
    if (mode === "dynamic" && values[k]) {
      previewValues[k] = `[${dynamicVarLabel(values[k])}]`;
    }
  });

  const headerText = hc?.format === "TEXT" ? _fillPreview(hc.text, vars.header, previewValues, "header") : null;
  const bodyText = bc?.text ? _fillPreview(bc.text, vars.body, previewValues, "body") : null;
  const footerText = fc?.text || null;

  return (
    <div className="wa-bc-preview">
      <div className="wa-bc-preview__label">Preview</div>
      <div className="wa-bc-preview__bubble">
        {headerText && <div className="wa-bc-preview__header">{headerText}</div>}
        {bodyText && <div className="wa-bc-preview__body">{bodyText}</div>}
        {footerText && <div className="wa-bc-preview__footer">{footerText}</div>}
        {btc?.buttons?.length > 0 && (
          <div className="wa-bc-preview__buttons">
            {btc.buttons.map((btn, i) => <div key={btn.text || i} className="wa-bc-preview__btn">{btn.text}</div>)}
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Automation Tab
   ═══════════════════════════════════════════════════════════════════════════

   One automation on screen at a time, picked from a dropdown in the card
   header. The events are already four and the list only grows, so the old
   stacked layout meant scrolling past three configs to reach the fourth — and
   past all of them to reach Save. Save now lives in a fixed footer, the same
   `sf-actionbar` pattern the student form uses.

   All events are still saved in a **single** PUT of the whole
   `whatsappAutomation` block, so switching the picker never drops the edits
   made to another event — they are all held in one `config` object and written
   together. */

/* ── Fee event variables ─────────────────────────────────────────────────────
   These describe *this payment / this installment* and have no equivalent on
   the Student, so they are listed separately and offered **in addition to** the
   full student catalog rather than instead of it. Mirrors FEE_EVENT_VARS in
   vc_be/utils/whatsappTemplateVars — the backend's save-time allow-list is the
   student catalog ∪ these, so anything offered here survives a save. */
const FEE_PAYMENT_VARS = [
  { value: "amount", label: "Amount (this payment)" },
  { value: "paid_date", label: "Payment date" },
  { value: "payment_mode", label: "Payment mode" },
  { value: "receipt_no", label: "Receipt / invoice no." },
  { value: "pending_total", label: "Pending after this payment" },
];
const FEE_REMINDER_VARS = [
  { value: "installment_label", label: "Installment label (EMI)" },
  { value: "amount", label: "Amount due (this installment)" },
  { value: "due_date", label: "Installment due date" },
  { value: "pending_total", label: "Total pending" },
];
const FEE_REMINDER_DEFAULT = {
  enabled: false, useTemplate: true, templateName: "", languageCode: "en", paramMapping: [], messageText: "",
};
const CLASS_STARTED_TEXT = "Hi {student_name}, your class for {course_name} ({batch_name}) has started! Join now from your dashboard.";
const CLASS_STARTED_DEFAULT = {
  enabled: false, useTemplate: true, templateName: "", languageCode: "en", paramMapping: [],
  messageText: CLASS_STARTED_TEXT,
};

function countTemplateParams(tpl) {
  const body = tpl?.components?.find((c) => c.type === "BODY")?.text || "";
  const matches = body.match(/\{\{(\d+)\}\}/g) || [];
  return matches.length ? Math.max(...matches.map((m) => parseInt(m.replace(/\D/g, ""), 10))) : 0;
}

/** Templates carry `parameterCount` from the BE list endpoint; fall back to
 *  counting `{{n}}` in the BODY so a template fetched without it still maps. */
const paramCountOf = (tpl) => (tpl ? (tpl.parameterCount ?? countTemplateParams(tpl)) : 0);

/**
 * The automation catalog. Adding an event is a row here plus its key in the
 * backend's `whatsappAutomation` block — no new UI.
 *
 *   eventVars   — variables specific to this event (the payment, the
 *                 installment), offered ABOVE the full student catalog rather
 *                 than instead of it. Every automation resolves the whole
 *                 student catalog server-side; an event only has to declare
 *                 what it adds.
 *   allowCustom — free-form text mode. Only legal inside Meta's 24h window, so
 *                 the fee events (which fire from a cron, hours or days after
 *                 any inbound message) deliberately don't offer it.
 *   managed     — sent by vc_be's whatsappAutomationService, which also reports
 *                 readiness, suggests a template, takes a test send and keeps
 *                 a log (GET /whatsapp/automations). Its event variables come
 *                 from that response, so they are defined once, server-side.
 *   audience    — "enquiry" maps the lead's catalog instead of the student's.
 *   reminder    — the event takes reminder offsets; `scoped` — quizzes vs exams.
 */
const MANAGED_DEFAULT = { enabled: false, templateName: "", languageCode: "en", paramMapping: [] };

const AUTOMATION_DEFS = [
  {
    key: "enquiryReceived",
    group: "Enquiries",
    label: "New Enquiry",
    icon: Inbox,
    tone: "blue",
    desc: "Sent to a lead as soon as their enquiry arrives — from your website, enquiry forms, Meta or Google ads, Zoho, a call, or added by your team.",
    managed: true,
    audience: "enquiry",
    defaults: { ...MANAGED_DEFAULT, skipManual: false },
  },
  {
    key: "studentWelcome",
    group: "Students",
    label: "Student Welcome",
    icon: UserPlus,
    tone: "green",
    desc: "Sent when a student is added, an enquiry is converted to a student, or a student joins with your invite link.",
    managed: true,
    defaults: MANAGED_DEFAULT,
  },
  {
    key: "studentEnrolled",
    group: "Students",
    label: "Enrolled in a Course",
    icon: GraduationCap,
    tone: "green",
    desc: "Sent when a student is enrolled in a course — Add to batch, a course added to their record, an approved request, a payment or a self-paced purchase. Course and batch fields are the ones they just joined. A student added together with a course gets the welcome only.",
    managed: true,
    defaults: MANAGED_DEFAULT,
  },
  {
    key: "classStarted",
    group: "Classes",
    label: "Class Started",
    icon: MessageCircle,
    tone: "blue",
    desc: "Automatically send a WhatsApp message to all batch students when an instructor starts a live class.",
    allowCustom: true,
    customVars: ["student_name", "course_name", "batch_name", "instructor_name", "institute_name"],
    defaults: CLASS_STARTED_DEFAULT,
    testKind: "batch",
  },
  {
    key: "assignmentPublished",
    group: "Assignments",
    label: "New Assignment",
    icon: ClipboardList,
    tone: "blue",
    desc: "Sent to every active student of the batch when an assignment is posted.",
    managed: true,
    defaults: MANAGED_DEFAULT,
  },
  {
    key: "assignmentDueSoon",
    group: "Assignments",
    label: "Assignment Due Soon",
    icon: AlarmClock,
    tone: "amber",
    desc: "Reminds the students who have not submitted yet, before the assignment is due.",
    managed: true,
    reminder: true,
    defaultOffsets: [1440],
    reminderHint: "An assignment due on a date without a time counts as due at 6 pm that day. A reminder whose time has passed is skipped, never sent late.",
    defaults: MANAGED_DEFAULT,
  },
  {
    key: "assessmentScheduled",
    group: "Quizzes & Exams",
    label: "Quiz / Exam Scheduled",
    icon: ClipboardCheck,
    tone: "blue",
    desc: "Sent to every student a quiz or exam is for when it is published — and again to anyone whose start time changes or who is added to it later.",
    managed: true,
    scoped: true,
    defaults: { ...MANAGED_DEFAULT, scope: "all" },
  },
  {
    key: "assessmentReminder",
    group: "Quizzes & Exams",
    label: "Quiz / Exam Reminder",
    icon: BellRing,
    tone: "amber",
    desc: "Reminds students before a scheduled quiz or exam starts. Anyone who has already started it is left out.",
    managed: true,
    reminder: true,
    scoped: true,
    defaultOffsets: [1440, 60],
    reminderHint: "Only a quiz or exam with a start date and time gets reminders. A reminder whose time has passed is skipped, never sent late.",
    defaults: { ...MANAGED_DEFAULT, scope: "all" },
  },
  {
    key: "feePaymentReceived",
    group: "Fees",
    label: "Payment Received",
    icon: Receipt,
    tone: "green",
    desc: "Sent to a student when a fee payment is recorded (Collect Fee or online).",
    eventVars: FEE_PAYMENT_VARS,
    eventVarsLabel: "This payment",
    defaults: FEE_REMINDER_DEFAULT,
  },
  {
    key: "feeDueSoon",
    group: "Fees",
    label: "Installment Due Soon",
    icon: CalendarClock,
    tone: "amber",
    desc: "Sent to a student a few days before an EMI installment's due date (email is always sent too).",
    eventVars: FEE_REMINDER_VARS,
    eventVarsLabel: "This installment",
    defaults: FEE_REMINDER_DEFAULT,
  },
  {
    key: "feeOverdue",
    group: "Fees",
    label: "Installment Overdue",
    icon: AlertTriangle,
    tone: "red",
    desc: "Sent when an EMI installment is past its due date and still unpaid.",
    eventVars: FEE_REMINDER_VARS,
    eventVarsLabel: "This installment",
    defaults: FEE_REMINDER_DEFAULT,
  },
];

const AUTOMATION_GROUPS = AUTOMATION_DEFS.reduce((acc, def) => {
  const bucket = acc.find((g) => g.group === def.group);
  if (bucket) bucket.items.push(def); else acc.push({ group: def.group, items: [def] });
  return acc;
}, []);

/** Stand-in values for the preview. Real values are resolved server-side per
 *  recipient at send time — this only has to look like a message. */
const PREVIEW_SAMPLES = {
  student_name: "John Doe", student_first_name: "John", student_mobile: "98765 43210",
  student_email: "john@example.com", student_status: "Active", guardian_name: "Robert Doe",
  course_name: "Java Full Stack Development", course_code: "JFS-01", course_duration: "6 months",
  course_mode: "Online", course_fees: "₹45,000", course_start_date: "01 Sep 2026",
  batch_name: "JFS_MORNING_02", batch_timing: "10:00 AM – 12:00 PM", batch_start_time: "10:00 AM",
  batch_mode: "Online", batch_room: "Lab 3", instructor_name: "Prof. Smith",
  institute_name: "Your Institute", brand_name: "Your Institute", legal_name: "Your Institute Pvt Ltd",
  institute_phone: "+91 98765 00000", institute_email: "info@example.com",
  portal_url: "https://portal.example.com",
  signup_link: "https://portal.example.com/signup?role=student&mobile=9876543210&email=john@example.com",
  pending_fees: "₹15,000", paid_fees: "₹30,000", total_fees: "₹45,000",
  installment_label: "Installment 2 of 4", amount: "₹7,500", due_date: "20 Aug 2026",
  pending_total: "₹15,000", paid_date: "13 Aug 2026", payment_mode: "UPI", receipt_no: "RCPT-1042",
  current_date: "13 Aug 2026", current_time: "10:30 AM", tomorrow_date: "14 Aug 2026",
  enquiry_name: "Meera Shah", enquiry_first_name: "Meera", enquiry_status: "Enquired",
  assignment_title: "Build a REST API", assignment_due: "Fri, 14 Aug 2026, 5:00 pm", assignment_due_date: "Fri, 14 Aug 2026",
  test_title: "Java Basics Quiz", test_start: "Sat, 15 Aug 2026, 10:00 am", test_start_date: "Sat, 15 Aug 2026",
  test_end: "Sat, 15 Aug 2026, 6:00 pm", test_duration: "45 minutes",
};
const sampleFor = (v) => PREVIEW_SAMPLES[v] || (v ? v.replace(/_/g, " ") : "");

/* WhatsApp's inline markup, so a template that reads `*Course*` previews bold
   instead of showing the asterisks. */
const WA_MARKUP = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g;
const RichText = ({ text }) => (
  <>
    {String(text || "").split(WA_MARKUP).map((part, i) => {
      if (/^\*[^*\n]+\*$/.test(part)) return <strong key={i}>{part.slice(1, -1)}</strong>;
      if (/^_[^_\n]+_$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>;
      if (/^~[^~\n]+~$/.test(part)) return <s key={i}>{part.slice(1, -1)}</s>;
      return <React.Fragment key={i}>{part}</React.Fragment>;
    })}
  </>
);

/* ── Automation picker (card header, right) ────────────────────────────────
   A custom menu rather than a <select> because the enabled/disabled state of
   each event is the thing you scan for, and a native option cannot carry a
   status dot. */
const AutomationPicker = ({ value, onChange, config }) => {
  const [open, setOpen] = useState(false);
  const active = AUTOMATION_DEFS.find((d) => d.key === value) || AUTOMATION_DEFS[0];
  const ActiveIcon = active.icon;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!e.target.closest(".wa-auto-picker")) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="wa-auto-picker">
      <button type="button" className={`wa-auto-picker__btn ${open ? "open" : ""}`} onClick={() => setOpen((p) => !p)}>
        <ActiveIcon size={15} />
        <span className="wa-auto-picker__label">{active.label}</span>
        <span className={`wa-auto-picker__dot ${config[active.key]?.enabled ? "on" : ""}`} />
        <ChevronDown size={15} className={`wa-auto-picker__chevron ${open ? "open" : ""}`} />
      </button>
      {open && (
        <div className="wa-auto-picker__menu">
          {AUTOMATION_GROUPS.map(({ group, items }) => (
            <div key={group} className="wa-auto-picker__group">
              <div className="wa-auto-picker__grouplabel">{group}</div>
              {items.map((def) => {
                const Icon = def.icon;
                return (
                  <button
                    key={def.key}
                    type="button"
                    className={`wa-auto-picker__item ${def.key === value ? "active" : ""}`}
                    onClick={() => { onChange(def.key); setOpen(false); }}
                  >
                    <Icon size={15} />
                    <span className="wa-auto-picker__itemlabel">{def.label}</span>
                    <span className={`wa-auto-picker__dot ${config[def.key]?.enabled ? "on" : ""}`} />
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Phone preview (editor, right column) ──────────────────────────────────
   Avatar is the favicon, not the logo: WhatsApp's profile picture is a 32px
   circle, and a wordmark-shaped logo crops to an unreadable smear in it. */
const AutomationPreview = ({ message, brandName, faviconUrl }) => {
  const initials = (brandName || "LMS").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const empty = !message || (!message.header && !message.body && !message.footer);

  return (
    <div className="wa-auto-preview">
      <div className="wa-auto-preview__head">
        <span className="wa-auto-preview__badge"><MessageCircle size={16} /></span>
        <div>
          <div className="wa-auto-preview__title">WhatsApp Message Preview</div>
          <div className="wa-auto-preview__sub">This is how your message will appear to recipients.</div>
        </div>
      </div>

      <div className="wa-phone">
        <div className="wa-phone__statusbar">
          <span className="wa-phone__clock">9:41</span>
          <span className="wa-phone__notch" />
          <span className="wa-phone__signals">
            <Signal size={12} /><Wifi size={12} /><BatteryFull size={14} />
          </span>
        </div>
        <div className="wa-phone__topbar">
          <ChevronLeft size={18} className="wa-phone__back" />
          {faviconUrl
            ? <img src={faviconUrl} alt="" className="wa-phone__avatar wa-phone__avatar--img" />
            : <span className="wa-phone__avatar">{initials}</span>}
          <div className="wa-phone__ident">
            <div className="wa-phone__name">
              {brandName || "Your Institute"} <BadgeCheck size={13} className="wa-phone__verified" />
            </div>
            <div className="wa-phone__acct">Business Account</div>
          </div>
          <MoreVertical size={16} className="wa-phone__more" />
        </div>
        <div className="wa-phone__chat">
          {empty ? (
            <div className="wa-phone__placeholder">Select a template or write a message to see the preview.</div>
          ) : (
            <div className="wa-phone__bubble">
              <div className="wa-phone__bcontent">
                {message.header && <div className="wa-phone__bhead"><RichText text={message.header} /></div>}
                {message.body && <div className="wa-phone__bbody"><RichText text={message.body} /></div>}
                {message.footer && <div className="wa-phone__bfoot">{message.footer}</div>}
                <span className="wa-phone__time">10:30 AM</span>
              </div>
              {message.buttons?.length > 0 && (
                <div className="wa-phone__btns">
                  {message.buttons.map((b, i) => <span key={b.text || i} className="wa-phone__btn">{b.text}</span>)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="wa-auto-preview__note">
        <Info size={15} />
        <div>
          <div className="wa-auto-preview__notetitle">Preview uses sample data</div>
          <div className="wa-auto-preview__notedesc">Real values will be used when the message is sent.</div>
        </div>
      </div>
    </div>
  );
};

/* ── Editor for one automation ─────────────────────────────────────────────── */
const AutomationEditor = ({ def, state, patch, templates, preview, eventVars: serverEventVars, readOnly, children }) => {
  const Icon = def.icon;
  // A managed event's own variables come from the server's catalog; the fee
  // events still declare theirs here.
  const eventVars = def.eventVars || serverEventVars || [];
  const selectedTpl = templates.find((t) => t.name === state.templateName);
  const paramCount = paramCountOf(selectedTpl);
  const useTemplate = def.allowCustom ? state.useTemplate : true;

  return (
    <div className="wa-auto-editor">
      <div className="wa-auto-editor__head">
        <div className={`wa-auto-editor__icon wa-auto-editor__icon--${def.tone}`}><Icon size={20} /></div>
        <div className="wa-auto-editor__headtext">
          <div className="wa-auto-editor__title">{def.label}</div>
          <div className="wa-auto-editor__desc">{def.desc}</div>
        </div>
        <label className="wa-auto-editor__toggle">
          <span>Enable</span>
          <Form.Check
            type="switch"
            id={`wa-auto-${def.key}`}
            checked={!!state.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
            disabled={readOnly}
          />
        </label>
      </div>

      {!state.enabled ? (
        <div className="wa-auto-editor__off">
          <Zap size={16} />
          This automation is off. Turn it on to configure the message it sends.
        </div>
      ) : (
        <div className="wa-auto-editor__cols">
          <div className="wa-auto-editor__form">
            {/* ── Message mode ── */}
            {def.allowCustom && (
              <div className="wa-mode-toggle">
                <button
                  type="button"
                  className={`wa-mode-btn ${useTemplate ? "wa-mode-btn--active" : ""}`}
                  onClick={() => patch({ useTemplate: true })}
                >
                  <FileText size={14} /> Meta Template
                </button>
                <button
                  type="button"
                  className={`wa-mode-btn ${!useTemplate ? "wa-mode-btn--active" : ""}`}
                  onClick={() => patch({ useTemplate: false })}
                >
                  <MessageCircle size={14} /> Custom Message
                </button>
              </div>
            )}

            {/* ── Template mode ── */}
            {useTemplate && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="wa-auto-label">Template</Form.Label>
                  <Form.Select
                    value={state.templateName}
                    onChange={(e) => {
                      const tpl = templates.find((t) => t.name === e.target.value);
                      const count = paramCountOf(tpl);
                      const existing = state.paramMapping || [];
                      patch({
                        templateName: e.target.value,
                        paramMapping: Array.from({ length: count }, (_, i) => existing[i] || ""),
                      });
                    }}
                  >
                    <option value="">Select an approved template…</option>
                    {templates.map((t) => (
                      <option key={t._id || t.name} value={t.name}>
                        {t.pending
                          ? `${t.name} — waiting for Meta's approval`
                          : `${t.name} (${t.language}) — ${paramCountOf(t)} param(s)`}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Choose a template and map each parameter to the data you want sent.
                  </Form.Text>
                </Form.Group>

                {/* ── Parameter mapping ── */}
                {selectedTpl && paramCount > 0 && (
                  <>
                    <Separator />
                    <div className="wa-auto-section">
                      <div className="wa-auto-section__title"><Settings size={14} /> Map Template Parameters</div>
                      <div className="wa-auto-section__hint">
                        Assign what data each template variable should contain. This must match your Meta-approved template.
                      </div>
                      <div className="wa-auto-params">
                        {Array.from({ length: paramCount }, (_, idx) => (
                          <div key={idx} className="wa-auto-param">
                            <span className="wa-auto-param__token">{`{{${idx + 1}}}`}</span>
                            <span className="wa-auto-param__arrow">→</span>
                            <Form.Select
                              value={(state.paramMapping || [])[idx] || ""}
                              onChange={(e) => {
                                const m = [...(state.paramMapping || [])];
                                while (m.length <= idx) m.push("");
                                m[idx] = e.target.value;
                                patch({ paramMapping: m });
                              }}
                            >
                              <option value="">— select variable —</option>
                              {/* Event-specific first — they are what the admin
                                  came here for; the ~50-field student catalog
                                  follows, grouped. */}
                              {eventVars.length > 0 && (
                                <optgroup label={def.eventVarsLabel || eventVars[0].group || "This event"}>
                                  {eventVars.map((v) => (
                                    <option key={v.value} value={v.value}>{v.label}</option>
                                  ))}
                                </optgroup>
                              )}
                              {def.audience === "enquiry" ? renderEnquiryVarOptions() : renderVarOptions()}
                            </Form.Select>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <Form.Group className="wa-auto-lang">
                  <Form.Label className="wa-auto-label">Language Code</Form.Label>
                  <Form.Text className="text-muted d-block mb-1">
                    Specify the language code for this template.
                  </Form.Text>
                  <Form.Control
                    value={state.languageCode}
                    onChange={(e) => patch({ languageCode: e.target.value })}
                    placeholder="en"
                  />
                </Form.Group>

                {!def.allowCustom && !def.managed && (
                  <div className="wa-auto-section__hint" style={{ marginTop: 12 }}>
                    Every student, course, batch and institute field is available here, plus the
                    payment-specific ones ({(def.eventVars || []).map((v) => v.label).join(", ")}).
                    WhatsApp is sent only when a template is set here <strong>and</strong> WhatsApp
                    notifications are enabled — an approved Meta template is required, free-form text
                    is not allowed.
                  </div>
                )}
              </>
            )}

            {/* ── Custom message mode ── */}
            {!useTemplate && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="wa-auto-label">Message Text</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={state.messageText}
                    onChange={(e) => patch({ messageText: e.target.value })}
                    placeholder="Type your message with placeholders…"
                  />
                  <Form.Text className="text-muted">
                    Available placeholders:{" "}
                    {(def.customVars || []).map((v, i) => (
                      <React.Fragment key={v}>{i > 0 && " "}<code>{`{${v}}`}</code></React.Fragment>
                    ))}
                  </Form.Text>
                </Form.Group>

                <div className="wa-alert wa-alert--warning">
                  <AlertTriangle size={13} />
                  Custom text messages only work within Meta's 24-hour customer service window.
                  Use a Meta Template if students haven't messaged your number recently.
                </div>
              </>
            )}

            {children}
          </div>

          <aside className="wa-auto-editor__side">{preview}</aside>
        </div>
      )}
    </div>
  );
};

/* ─── Automation Tab ───────────────────────────────────────────────────────── */
const AutomationTab = ({ showToast }) => {
  const canManage = can("whatsapp.manage");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState(AUTOMATION_DEFS[0].key);
  const [config, setConfig] = useState(() =>
    Object.fromEntries(AUTOMATION_DEFS.map((d) => [d.key, { ...d.defaults }])));
  /* Serialized last-saved state. Compared against `config` to decide whether the
     footer shows "Unsaved changes" — cheap here because the whole block is a
     dozen small objects. */
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const branding = useBranding();

  /* The institute-wide master switch (`InstituteSettings.whatsappNotifications`).
     It gates every automation on the server, it defaults to OFF, and it lives on
     Settings → Notifications — so an admin can configure an event here, pick an
     approved template, map every parameter, save, and still have nothing sent,
     with no indication why. Surfaced (and switchable) here for that reason. */
  const [masterOn, setMasterOn] = useState(true);
  const [togglingMaster, setTogglingMaster] = useState(false);

  // Test message state (class-started only — it is the only event with a test endpoint)
  const [batches, setBatches] = useState([]);
  const [testBatchId, setTestBatchId] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  /* The managed events' own state, from GET /whatsapp/automations: whether each
     can send and why not, what it sent lately, and the catalog (event variables,
     reminder offsets, suggested templates). The tab still works without it. */
  const [autoStatus, setAutoStatus] = useState(null);
  const [suggestedBusy, setSuggestedBusy] = useState(null); // the ready-made template's id being set up
  const [eventTestBusy, setEventTestBusy] = useState(false);

  const loadAutomationStatus = useCallback(async () => {
    try {
      const res = await apiClient.get("/whatsapp/automations");
      setAutoStatus(res.data);
    } catch { /* readiness is a convenience, not a dependency */ }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await apiClient.get("/whatsapp/templates");
      setTemplates((res.data.templates || []).filter((t) => t.status === "APPROVED"));
    } catch { /* keep the list already on screen */ }
  }, []);

  // Load settings + templates + batches on mount
  useEffect(() => {
    loadAutomationStatus();
    (async () => {
      try {
        const [settingsRes, tplRes, batchRes] = await Promise.allSettled([
          apiClient.get("/institute/settings"),
          apiClient.get("/whatsapp/templates"),
          apiClient.get("/batches/list"),
        ]);
        if (settingsRes.status === "fulfilled") {
          setMasterOn(!!settingsRes.value.data.settings?.whatsappNotifications);
          const auto = settingsRes.value.data.settings?.whatsappAutomation || {};
          const next = Object.fromEntries(AUTOMATION_DEFS.map((d) => {
            const saved = auto[d.key] || {};
            return [d.key, {
              ...d.defaults,
              ...saved,
              // `useTemplate` is tri-state on the wire: absent means "template",
              // which a plain spread would read as undefined → falsy → custom.
              useTemplate: saved.useTemplate !== false,
              paramMapping: saved.paramMapping || [],
              messageText: saved.messageText || d.defaults.messageText,
            }];
          }));
          setConfig(next);
          setSavedSnapshot(JSON.stringify(next));
        }
        if (tplRes.status === "fulfilled") {
          setTemplates((tplRes.value.data.templates || []).filter(t => t.status === "APPROVED"));
        }
        if (batchRes.status === "fulfilled") {
          const bd = batchRes.value.data;
          setBatches(bd?.batches || bd || []);
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [loadAutomationStatus]);

  const patch = useCallback((key, updates) => {
    setConfig((prev) => ({ ...prev, [key]: { ...prev[key], ...updates } }));
  }, []);

  const serialized = JSON.stringify(config);
  const dirty = !!savedSnapshot && serialized !== savedSnapshot;

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put("/institute/settings", { whatsappAutomation: config });
      setSavedSnapshot(serialized);
      showToast("Automation settings saved!");
      loadAutomationStatus();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save", "error");
    }
    setSaving(false);
  };

  /* Written on its own rather than folded into handleSave: the master switch is
     not part of `config`, and an admin who has just been told "nothing will send"
     should be able to fix it without also committing whatever half-finished
     mapping is on screen. */
  const handleEnableMaster = async () => {
    setTogglingMaster(true);
    try {
      await apiClient.put("/institute/settings", { whatsappNotifications: true });
      setMasterOn(true);
      showToast("WhatsApp notifications enabled");
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not enable WhatsApp notifications", "error");
    }
    setTogglingMaster(false);
  };

  const handleSendTest = async () => {
    if (!testBatchId) {
      showToast("Select a batch first", "error");
      return;
    }
    setSendingTest(true);
    try {
      const payload = { batchId: testBatchId };
      if (testPhone.trim()) payload.phone = testPhone.trim();
      const res = await apiClient.post("/whatsapp/test-class-notification", payload);
      showToast(res.data.message || "Test sent!");
    } catch (err) {
      showToast(err?.response?.data?.message || "Test send failed", "error");
    }
    setSendingTest(false);
  };

  /* Submit one of the event's ready-made templates to Meta (unless the account
     already has it) and select it with its mapping. The server saves the three
     fields itself, so they are written into the saved snapshot too — they are
     not "unsaved changes". */
  const handleUseSuggested = async (key, variant) => {
    setSuggestedBusy(variant || "default");
    try {
      const res = await apiClient.post(`/whatsapp/automations/${key}/suggested-template`, { variant });
      const chosen = { templateName: res.data.templateName, languageCode: "en", paramMapping: res.data.mapping || [] };
      patch(key, chosen);
      setSavedSnapshot((prev) => {
        if (!prev) return prev;
        const saved = JSON.parse(prev);
        return JSON.stringify({ ...saved, [key]: { ...saved[key], ...chosen } });
      });
      showToast(res.data.message || "Template submitted");
      loadTemplates();
      loadAutomationStatus();
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not set up the template", "error");
    }
    setSuggestedBusy(null);
  };

  /* Tests what is on screen — the chosen template and mapping — saved or not. */
  const handleEventTest = async (key, phone) => {
    setEventTestBusy(true);
    try {
      const { templateName, languageCode, paramMapping } = config[key] || {};
      const res = await apiClient.post(`/whatsapp/automations/${key}/test`, {
        phone, draft: { templateName, languageCode, paramMapping },
      });
      showToast(res.data.message || "Test message sent");
    } catch (err) {
      showToast(err?.response?.data?.message || "Test send failed", "error");
    }
    setEventTestBusy(false);
  };

  const def = AUTOMATION_DEFS.find((d) => d.key === activeKey) || AUTOMATION_DEFS[0];
  const state = config[def.key];
  const serverEvent = autoStatus?.catalog?.events?.[def.key] || null;

  /* The approved templates, plus the selected one when it is not among them —
     a ready-made template waiting for Meta, or one that lost its approval. A
     select whose value is missing from its options shows nothing, which reads
     as "no template" when there is one. */
  const editorTemplates = useMemo(() => {
    if (!state?.templateName || templates.some((t) => t.name === state.templateName)) return templates;
    const readyMade = (serverEvent?.templates || []).find((t) => t.name === state.templateName);
    return [...templates, {
      _id: `pending-${state.templateName}`,
      name: state.templateName,
      language: state.languageCode || "en",
      pending: true,
      components: readyMade ? [{ type: "BODY", text: readyMade.body }] : [],
    }];
  }, [templates, state, serverEvent]);

  /* The readiness the server reports is about the SAVED settings. */
  const eventDirty = useMemo(() => {
    if (!savedSnapshot) return false;
    try {
      return JSON.stringify(JSON.parse(savedSnapshot)[def.key]) !== JSON.stringify(state);
    } catch {
      return false;
    }
  }, [savedSnapshot, def.key, state]);

  /* What the phone mock renders: the template body with each mapped variable
     swapped for a sample, or the custom text with its {placeholders} filled. */
  const previewMessage = useMemo(() => {
    if (!state) return null;
    if (def.allowCustom && !state.useTemplate) {
      let text = state.messageText || "";
      (def.customVars || []).forEach((v) => {
        text = text.replace(new RegExp(`\\{${v}\\}`, "g"), sampleFor(v));
      });
      return { body: text };
    }
    const tpl = editorTemplates.find((t) => t.name === state.templateName);
    if (!tpl) return null;
    const comp = (type) => tpl.components?.find((c) => c.type === type);
    const fill = (text) => String(text || "").replace(/\{\{(\d+)\}\}/g, (m, n) => {
      const v = (state.paramMapping || [])[Number(n) - 1];
      return v ? sampleFor(v) : m;
    });
    return {
      header: comp("HEADER")?.format === "TEXT" || !comp("HEADER")?.format ? fill(comp("HEADER")?.text) : "",
      body: fill(comp("BODY")?.text),
      footer: comp("FOOTER")?.text || "",
      buttons: comp("BUTTONS")?.buttons || [],
    };
  }, [def, state, editorTemplates]);

  if (loading) return <Loader />;

  return (
    <div className="wa-automation">
      {/* The one thing worth interrupting for: with this off, everything below
          saves correctly and sends nothing. */}
      {!masterOn && (
        <div className="wa-alert wa-alert--warning wa-auto-master">
          <AlertTriangle size={16} />
          <div className="wa-auto-master__text">
            <strong>WhatsApp notifications are turned off for this institute.</strong>
            <span>
              Automations below will save, but no message is sent until this is on.
              It also lives under Settings → Notifications.
            </span>
          </div>
          {canManage && (
            <AppButton size="sm" onClick={handleEnableMaster} disabled={togglingMaster}>
              {togglingMaster ? "Enabling…" : "Turn on"}
            </AppButton>
          )}
        </div>
      )}

      {/* overflowVisible: the picker menu drops out of the header and the card's
          default clip would cut it off. */}
      <AppCard overflowVisible>
        <AppCardHeader>
          <div className="wa-auto-header">
            <div className="wa-auto-header__text">
              <AppCardTitle><Zap size={18} /> WhatsApp Automation</AppCardTitle>
              <p className="settings-card-desc">
                Configure automatic WhatsApp messages triggered by platform events.
                Pick an automation to set up its message.
              </p>
            </div>
            <AutomationPicker value={activeKey} onChange={setActiveKey} config={config} />
          </div>
        </AppCardHeader>
        <AppCardContent>
          <AutomationEditor
            key={def.key}
            def={def}
            state={state}
            patch={(updates) => patch(def.key, updates)}
            templates={editorTemplates}
            eventVars={serverEvent?.eventVars}
            readOnly={!canManage}
            preview={
              <AutomationPreview
                message={previewMessage}
                brandName={branding.brandName}
                faviconUrl={branding.faviconUrl}
              />
            }
          >
            {def.managed && (
              <AutomationEventPanel
                def={def}
                state={state}
                patch={(updates) => patch(def.key, updates)}
                dirty={eventDirty}
                status={autoStatus?.events?.[def.key]}
                catalog={serverEvent}
                offsetMenu={autoStatus?.catalog?.offsetMenu}
                templateStatus={autoStatus?.templateStatus}
                onUseSuggested={canManage ? (variant) => handleUseSuggested(def.key, variant) : undefined}
                suggestedBusy={suggestedBusy}
                onTest={canManage ? (phone) => handleEventTest(def.key, phone) : undefined}
                testBusy={eventTestBusy}
              />
            )}
            {canManage && def.testKind === "batch" && (
              <>
                <Separator />
                <div className="wa-auto-section">
                  <div className="wa-auto-section__title"><Send size={14} /> Send Test Message</div>
                  <div className="wa-auto-test">
                    <Form.Group className="wa-auto-test__batch">
                      <Form.Label className="wa-auto-label">Batch</Form.Label>
                      <Form.Select value={testBatchId} onChange={(e) => setTestBatchId(e.target.value)}>
                        <option value="">Select a batch…</option>
                        {batches.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="wa-auto-test__phone">
                      <Form.Label className="wa-auto-label">Phone (optional)</Form.Label>
                      <Form.Control
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        placeholder="e.g. 9876543210"
                      />
                    </Form.Group>
                    <AppButton onClick={handleSendTest} disabled={sendingTest || !testBatchId}>
                      {sendingTest ? <Spinner animation="border" size="sm" /> : <><Send size={14} /> Send Test Message</>}
                    </AppButton>
                  </div>
                  <div className="wa-auto-section__hint">
                    Sends one message using current settings. Leave phone blank to send to the first student in the batch.
                  </div>
                </div>
              </>
            )}
          </AutomationEditor>
        </AppCardContent>
      </AppCard>

      {/* ── Fixed bottom action bar (mirrors the student form's sf-actionbar) ── */}
      {canManage && (
        <div className="wa-auto-actionbar">
          {dirty && <span className="wa-auto-actionbar__dirty">Unsaved changes</span>}
          <AppButton onClick={handleSave} disabled={saving}>
            {saving
              ? <><Spinner animation="border" size="sm" /> Saving…</>
              : <><Save size={16} /> Save Automation Settings</>}
          </AppButton>
        </div>
      )}
    </div>
  );
};

/* ─── Config Tab ───────────────────────────────────────────────────────────── */
const ConfigTab = ({ showToast, connection }) => {
  /**
   * The Pages grant belongs to lead capture, which is its own plan feature —
   * separate from ads publishing and from WhatsApp. An institute without it has
   * nothing to connect Pages *for*, and the grant endpoint would refuse the
   * code anyway, so the prompt below stays hidden rather than offering a button
   * that ends in a plan error.
   */
  const { hasPlanFeature } = usePlan();
  const canCaptureLeads = hasPlanFeature("leadCapture") || hasPlanFeature("adsManager");
  // Owned by the dashboard, which needs the same answer to choose its landing tab.
  const { status, setStatus, loading, error: statusError, refresh, canManage } = connection;
  const [disconnecting, setDisconnecting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  // A Facebook window we opened is still out there, and this page will not be
  // told when it finishes — the guide offers a re-check while this is set.
  const [awaitingReturn, setAwaitingReturn] = useState(false);
  const [checking, setChecking] = useState(false);
  /**
   * Set when WhatsApp connected through Embedded Signup, whose token cannot
   * see a Facebook Page. Without the prompt the owner has no way to know that
   * their lead forms are not being captured -- the WhatsApp side looks fine.
   * The status read is authoritative and survives a reload; the connect
   * response only gets the first word in.
   */
  const [grantHint, setGrantHint] = useState(false);
  const needsBusinessGrant = typeof status?.needsBusinessGrant === "boolean"
    ? status.needsBusinessGrant
    : grantHint;
  const [syncing, setSyncing] = useState(false);
  const [repairing, setRepairing] = useState(false);

  /** Re-read after a change, without a loader; fall back to a visible load. */
  const loadStatus = useCallback(async () => {
    const next = await refresh({ silent: true });
    if (!next) await refresh();
    return next;
  }, [refresh]);

  // Asked live rather than read off `status`: the quality and tier stored on the
  // config are a snapshot taken at connect time and are never written again.
  const fetchConfigSendingHealth = useCallback(async (force) => {
    const { data } = await apiClient.get(`/whatsapp/health${force ? "?refresh=1" : ""}`);
    return data;
  }, []);

  /**
   * Open the WhatsApp connect dialog.
   *
   * Two flows, chosen by the backend (`flow`), never guessed here:
   *
   *   embedded_signup  Meta's guided dialog via the JS SDK. The customer can
   *                    create the WABA, verify the number and set the display
   *                    name inside the popup, so none of the manual Business
   *                    Manager setup is needed. Requires FB.login({config_id})
   *                    - Meta does not honour config_id on a redirect URL.
   *   oauth_redirect   The original dialog. Shares assets the customer already
   *                    created, and carries the Pages scopes in the same grant.
   *
   * The split matters beyond the popup: an Embedded Signup token is
   * WhatsApp-only, so lead capture moves to its own grant
   * (`handleConnectBusiness`). On the redirect flow both still travel together,
   * which is what keeps every already-connected institute behaving as before.
   */
  const handleConnect = async () => {
    /**
     * The fetch and the launch are caught separately on purpose.
     *
     * One `try` around both reported "Failed to load connect configuration" for
     * any error at all — including a Facebook SDK failure that happened long
     * after the config had returned 200. That message sends whoever reads it to
     * inspect a request that was fine, which is worse than no message.
     */
    let data;
    try {
      ({ data } = await apiClient.get("/whatsapp/connect/config"));
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to load connect configuration.", "error");
      return;
    }

    if (!data.configured || !data.appId) {
      showToast(
        data.message || "WhatsApp Embedded Signup is not configured by the administrator yet.",
        "error",
      );
      return;
    }

    try {
      if (data.flow === "embedded_signup") {
        await launchEmbeddedSignup(data);
        return;
      }
      openRedirectDialog(data);
    } catch (err) {
      // Say what actually broke. Facebook's own wording ("Invalid version
      // specified", "Can't load URL") is the entire diagnosis, and burying it
      // costs an hour of looking in the wrong place.
      console.error("WhatsApp connect failed to launch", err);
      showToast(`Could not open the Facebook window: ${err?.message || err}`, "error");
    }
  };

  /**
   * The original OAuth dialog: one grant covering WhatsApp and Pages together.
   *
   * Extracted so Embedded Signup can fall back to it. That fallback is the
   * point — the SDK is a third-party script from connect.facebook.net, which is
   * a routine ad-blocker target, and without somewhere to fall back to a
   * blocked script leaves the Connect button doing nothing at all.
   */
  const openRedirectDialog = (data) => {
    const scope = [
      ...new Set([...(data.whatsappScopes || []), ...(data.businessScopes || [])]),
    ].join(",");

    const redirectUri = data.redirectUri || `${window.location.origin}/whatsapp/connect/callback`;
    // auth_type=rerequest - without it Facebook skips re-prompting for scopes
    // it has already settled for this user, so a re-connect silently mints a
    // token with the OLD permission set.
    let url = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${data.appId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&response_type=code&auth_type=rerequest`;

    /**
     * The signed return address, present only when the dialog is coming back to
     * the canonical relay rather than to this origin. It is the only thing that
     * survives the round trip through Meta, so it is how the relay knows which
     * of hundreds of institute domains to send the browser home to.
     */
    if (data.state) {
      url += `&state=${encodeURIComponent(data.state)}`;
    }

    // `window.open` returns null when the browser blocks it, and without this
    // the Connect button simply does nothing — the most common failed connect.
    const popup = window.open(url, "meta_wa_signup", "width=600,height=700,scrollbars=yes");
    if (!popup) {
      showToast(
        "Your browser blocked the Facebook window. Allow pop-ups for this site, then click Connect again.",
        "error",
      );
      return;
    }
    setAwaitingReturn(true);
  };

  const handleCheckAgain = async () => {
    setChecking(true);
    const next = await refresh({ silent: true });
    setChecking(false);
    if (next?.connected) {
      setAwaitingReturn(false);
      showToast("WhatsApp is connected.");
    } else if (next) {
      showToast("Not connected yet. Finish the steps in the Facebook window, then check again.", "info");
    } else {
      showToast("Could not check the connection. Try again in a moment.", "error");
    }
  };

  // For an admin who cannot connect: the owner needs this exact page.
  const handleCopyLink = async () => {
    const url = `${window.location.origin}/whatsapp#config`;
    try {
      await navigator.clipboard.writeText(url);
      showToast("Link copied. Send it to your Super Admin.");
    } catch {
      showToast(`Copy this link: ${url}`, "info");
    }
  };

  /**
   * Redeem an Embedded Signup code.
   *
   * Split out because `FB.login`'s callback may not be async — see the note at
   * the call site. Everything that needs `await` lives here instead.
   */
  const exchangeEmbeddedSignupCode = async (code) => {
    setConnecting(true);
    try {
      const { data } = await apiClient.post("/whatsapp/connect/callback", {
        code,
        flow: "embedded_signup",
      });
      showToast(data.message || "WhatsApp connected.", "success");
      // Embedded Signup grants WhatsApp only - leads need their own click.
      if (data.needsBusinessGrant) setGrantHint(true);
      await loadStatus();
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to connect WhatsApp. Please try again.",
        "error",
      );
    } finally {
      setConnecting(false);
    }
  };

  /**
   * Meta's guided WhatsApp onboarding.
   *
   * The `message` listener is not decoration: on cancel the popup simply closes
   * and FB.login reports nothing useful, so without it the only thing this
   * screen could say is "something went wrong". Meta's own step events name
   * where the customer stopped.
   */
  const launchEmbeddedSignup = async (config) => {
    /**
     * Launch from the canonical host when there is a signed `state`.
     *
     * `FB.login` only runs on a domain listed in the app console's Allowed
     * Domains for the JavaScript SDK, and that list takes no wildcards — so
     * calling it from this page would need every institute subdomain and custom
     * domain registered by hand, which is the trap the OAuth relay already
     * exists to avoid. The popup runs the dialog on the one whitelisted host and
     * sends the code back here.
     *
     * No `state` means the backend saw no reason to mint one: this institute is
     * already served from the canonical host, so the SDK can run right here.
     */
    if (config.state) {
      const url = new URL("/whatsapp/connect/embedded", config.redirectUri || window.location.origin);
      url.searchParams.set("state", config.state);
      /**
       * Navigate this tab rather than opening a popup.
       *
       * `FB.login` always opens a window of its own, so a popup here would put
       * the customer in front of three: their dashboard, our launcher, and
       * Facebook. Taking over the tab means they only ever see two — their tab
       * and the Facebook dialog — which is exactly what the plain OAuth flow
       * looks like. The launcher sends them home when it is done.
       */
      window.location.assign(url.toString());
      return;
    }

    let FB;
    try {
      FB = await loadFacebookSdk(config.appId, config.graphVersion);
    } catch (err) {
      /**
       * Degrade to the redirect dialog rather than dead-ending.
       *
       * A blocked or unreachable SDK is the one Embedded Signup failure the
       * customer can do nothing about, and it is not rare — an ad blocker is
       * enough. The old dialog needs no third-party script and still connects;
       * it only costs the customer the guided WABA setup. A working connect
       * beats a nicer one that never opens.
       */
      showToast(
        "Facebook's setup window could not load, so we opened the standard connect instead.",
        "info",
      );
      openRedirectDialog(config);
      return;
    }

    let lastEvent = null;
    const unsubscribe = onEmbeddedSignupEvent((payload) => { lastEvent = payload; });

    /**
     * A PLAIN function, never `async`.
     *
     * The SDK type-checks this argument and rejects a promise-returning one
     * outright: "Expression is of type asyncfunction, not function". The await
     * work is therefore done in a helper the callback fires and forgets.
     */
    FB.login(
      (response) => {
        unsubscribe();

        const code = response?.authResponse?.code;
        if (!code) {
          // lastEvent is the difference between a useful message and a shrug.
          const step = lastEvent?.data?.current_step;
          const reason =
            lastEvent?.data?.error_message ||
            (step
              ? `Setup was closed at the "${String(step).replace(/_/g, " ").toLowerCase()}" step.`
              : "Setup was cancelled before it finished.");
          showToast(reason, "error");
          return;
        }

        exchangeEmbeddedSignupCode(code);
      },
      {
        config_id: config.configId,
        response_type: "code",
        override_default_response_type: true,
        extras: { setup: {}, featureType: "", sessionInfoVersion: "3" },
      },
    );
  };

  /**
   * The second grant: Facebook Pages and Lead Forms today, ad management once
   * Meta's review clears it.
   *
   * Deliberately a separate click. An Embedded Signup token cannot see a Page,
   * so this cannot be folded back into the WhatsApp dialog - and keeping it
   * separate is also what lets ads be added later by widening the scope list
   * server-side, with nothing to ship here.
   */
  const handleConnectBusiness = async () => {
    try {
      // Third entry point to the same grant, so it goes through the same helper
      // as Lead Sources and Ad Accounts — one URL construction, one signed
      // state, and scopes chosen server-side from the institute's plan.
      await startMetaBusinessConnect({ from: "lead-sources" });
    } catch (err) {
      showToast(
        err?.response?.data?.message || err.message || "Failed to load connect configuration.",
        "error",
      );
    }
  };

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const handleDisconnect = () => setShowDisconnectConfirm(true);

  const confirmDisconnect = async () => {
    setShowDisconnectConfirm(false);
    setDisconnecting(true);
    try {
      await apiClient.post("/whatsapp/disconnect");
      showToast("WhatsApp disconnected");
      // The re-read brings the connect flow for the guide. If it fails, say
      // disconnected anyway — that much the 200 above already proved.
      const next = await refresh({ silent: true });
      if (!next) {
        setStatus((prev) => ({
          connected: false,
          canManage: prev?.canManage,
          previouslyConnected: true,
          previousPhoneNumber: prev?.displayPhoneNumber || "",
        }));
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Disconnect failed", "error");
    }
    setDisconnecting(false);
  };

  const handleSyncTemplates = async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post("/whatsapp/templates/sync");
      showToast(data.message || "Templates synced");
    } catch (err) {
      showToast(err?.response?.data?.message || "Sync failed", "error");
    }
    setSyncing(false);
  };

  const handleRepairWebhook = async () => {
    setRepairing(true);
    try {
      const { data } = await apiClient.post("/whatsapp/connect/repair-webhook");
      showToast(data.message || "Webhook repaired successfully");
    } catch (err) {
      showToast(err?.response?.data?.message || "Repair failed", "error");
    }
    setRepairing(false);
  };

  if (!status && loading) return <Loader />;

  /*
    No answer is not "not connected". This used to swallow the error and render
    the connect screen, which told an admin whose request had been refused that
    a working WhatsApp number was not connected.
  */
  if (!status) {
    return (
      <AppCard>
        <AppCardContent>
          <Callout tone="danger" role="alert">
            Could not check the WhatsApp connection.
            {" "}{statusError?.response?.data?.message || "Check your internet connection and try again."}
          </Callout>
          <div className="wa-config-actions">
            <AppButton size="sm" onClick={() => refresh()}>
              <RefreshCw size={16} /> Try again
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>
    );
  }

  /* ── Not connected ── */
  if (!status.connected) {
    return (
      <WhatsAppConnectGuide
        flow={status.connectFlow}
        canManage={canManage}
        connectAvailable={status.connectAvailable !== false}
        previousPhoneNumber={status.previouslyConnected ? status.previousPhoneNumber : ""}
        busy={connecting}
        awaitingReturn={awaitingReturn}
        checking={checking}
        onConnect={handleConnect}
        onCheckAgain={handleCheckAgain}
        onCopyLink={handleCopyLink}
      />
    );
  }

  /* ── Connected ── */
  const qualityColor = { GREEN: 'emerald', YELLOW: 'amber', RED: 'rose' }[status.qualityRating] || 'blue';

  return (
    <div className="wa-config-connected">
      {/*
        The live sending verdict, not a stored one.

        This replaced a flat green "WhatsApp is connected" banner. Holding a
        valid config and being able to deliver a message are different facts,
        and the banner asserted the second while only ever checking the first —
        so an account with business verification pending or no payment method
        on it read as fully healthy while every template send was being dropped.
        Which flow connected it is still on the Connection Details card below.
      */}
      <WhatsAppSendingStatus variant="card" fetchHealth={fetchConfigSendingHealth} />

      {/*
        Embedded Signup authorises WhatsApp and nothing else, so Facebook and
        Instagram lead forms stay dark until a second grant is given. Said here
        rather than left to be discovered: from the owner's side the connect
        looked complete, and the only symptom is leads that never arrive.
      */}
      {needsBusinessGrant && canCaptureLeads && (
        <div className="wa-alert wa-alert--warning wa-alert--action">
          <Link2 size={14} />
          <span>
            Facebook Pages are not connected. Lead forms from Facebook and Instagram
            will not reach Enquiries until you authorise them.
          </span>
          <AppButton size="sm" variant="outline" onClick={handleConnectBusiness}>
            Connect Facebook Pages
          </AppButton>
        </div>
      )}

      {/* Info cards */}
      <StatsGrid className="wa-stats-grid">
        <StatsCard
          title="Phone Number"
          value={status.displayPhoneNumber || status.phoneNumberId}
          icon={Phone}
          iconColor="blue"
        />
        <StatsCard
          title="Quality Rating"
          value={status.qualityRating || 'N/A'}
          icon={Gauge}
          iconColor={qualityColor}
        />
        <StatsCard
          title="Messaging Tier"
          value={status.messagingLimit ? status.messagingLimit.replace('TIER_', '').replace('K', 'K/day') : 'N/A'}
          icon={Zap}
          iconColor="violet"
        />
        <StatsCard
          title="Connected Since"
          value={status.connectedAt ? new Date(status.connectedAt).toLocaleDateString() : 'N/A'}
          icon={Calendar}
          iconColor="amber"
        />
      </StatsGrid>

      {/* Details card */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Settings size={18} /> Connection Details</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="wa-detail-grid">
            {/* Withheld by the server from anyone who cannot act on them. */}
            {status.wabaId && (
              <div className="wa-detail-row">
                <span className="wa-detail-label">WABA ID</span>
                <span>{status.wabaId}</span>
              </div>
            )}
            {status.phoneNumberId && (
              <div className="wa-detail-row">
                <span className="wa-detail-label">Phone Number ID</span>
                <span>{status.phoneNumberId}</span>
              </div>
            )}
            <div className="wa-detail-row">
              <span className="wa-detail-label">Daily Message Limit</span>
              <span>{status.dailyMessageLimit || 1000}</span>
            </div>
            <div className="wa-detail-row">
              <span className="wa-detail-label">Connected Via</span>
              <span>
                <StatusBadge status="active">
                  {status.connectedVia === 'embedded_signup' ? 'Embedded Signup' : 'Manual'}
                </StatusBadge>
              </span>
            </div>
          </div>

          <Separator />

          <div className="wa-config-actions">
            {can("whatsapp.manage") && (
              <AppButton size="sm" onClick={handleSyncTemplates} disabled={syncing}>
                <RefreshCw size={14} className={syncing ? 'wa-spin' : ''} />
                {syncing ? 'Syncing...' : 'Sync Templates'}
              </AppButton>
            )}
            {/* The server refuses these to anyone without the `config` right. */}
            {canManage && (
              <>
                <AppButton variant="outline" size="sm" onClick={handleRepairWebhook} disabled={repairing}>
                  <RefreshCw size={14} className={repairing ? 'wa-spin' : ''} />
                  {repairing ? 'Repairing...' : 'Repair Webhook'}
                </AppButton>
                <AppButton variant="outline" size="sm" onClick={handleConnect} disabled={connecting}>
                  <Link2 size={14} /> Reconnect
                </AppButton>
                <AppButton variant="destructive" size="sm" onClick={handleDisconnect} disabled={disconnecting}>
                  <Unplug size={14} /> {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </AppButton>
              </>
            )}
          </div>
          {!canManage && (
            <div className="wa-config-actions">
              <Callout tone="info">
                Reconnecting, repairing or disconnecting WhatsApp is done by your institute's Super Admin.
              </Callout>
            </div>
          )}
        </AppCardContent>
      </AppCard>


      <ConfirmModal
        show={showDisconnectConfirm}
        onHide={() => setShowDisconnectConfirm(false)}
        onConfirm={confirmDisconnect}
        title="Disconnect WhatsApp"
        message="Are you sure you want to disconnect WhatsApp? Notifications will stop sending."
        confirmText="Disconnect"
        variant="danger"
      />
    </div>
  );
};

/* ─── Opt-In Management Tab ────────────────────────────────────────────────── */
const OptInTab = ({ showToast }) => {
  const canReply = can("whatsapp.reply");
  const canManage = can("whatsapp.manage");
  const [search, setSearch] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkIds, setBulkIds] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);

  const checkStatus = async () => {
    if (!search.trim()) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/whatsapp/optin/status/${search.trim()}`);
      setResult(data);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to check", "error");
      setResult(null);
    }
    setLoading(false);
  };

  const recordOptIn = async (studentId) => {
    try {
      await apiClient.post("/whatsapp/optin", { studentId, phone: result?.record?.phone || "", method: "manual_admin" });
      showToast("Opt-in recorded");
      checkStatus();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed", "error");
    }
  };

  const recordOptOut = async (studentId) => {
    try {
      await apiClient.post("/whatsapp/optout", { studentId, reason: "admin_action" });
      showToast("Opt-out recorded");
      checkStatus();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed", "error");
    }
  };

  const handleBulkOptIn = async () => {
    const ids = bulkIds.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    if (!ids.length) { showToast("Enter at least one Student ID", "warning"); return; }
    if (ids.length > 500) { showToast("Maximum 500 students per bulk operation", "warning"); return; }
    setBulkLoading(true);
    try {
      const { data } = await apiClient.post("/whatsapp/optin/bulk", { studentIds: ids });
      showToast(data.message || `Opt-in recorded for ${data.total} student(s)`);
      setBulkIds("");
    } catch (err) {
      showToast(err?.response?.data?.message || "Bulk opt-in failed", "error");
    }
    setBulkLoading(false);
  };

  return (
    <>
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Shield size={18} /> Check Individual Student</AppCardTitle>
          <p className="settings-card-desc">
            WhatsApp messages are only sent to students who have opted in. Look up consent status here.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="wa-optin-search">
            <div className="settings-field" style={{ flex: 1, maxWidth: 320 }}>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Enter Student ID to check..."
                onKeyDown={e => { if (e.key === "Enter") checkStatus(); }}
              />
            </div>
            <AppButton size="sm" onClick={checkStatus} disabled={loading}>
              <Search size={13} /> {loading ? "Checking..." : "Check"}
            </AppButton>
          </div>

          {result && (
            <div className="wa-optin-result">
              <div className="wa-optin-result__info">
                <span>Opt-In Status:</span>
                <StatusBadge status={result.optedIn ? "active" : "inactive"}>
                  {result.optedIn ? "Opted In" : "Not Opted In"}
                </StatusBadge>
                {result.record?.optInAt && (
                  <span className="wa-text-muted wa-text-sm">
                    since {new Date(result.record.optInAt).toLocaleDateString()}
                  </span>
                )}
                {result.record?.optInMethod && (
                  <span className="wa-text-muted wa-text-sm">via {result.record.optInMethod}</span>
                )}
              </div>
              {canReply && (
                <div>
                  {result.optedIn ? (
                    <AppButton variant="outline" size="sm" onClick={() => recordOptOut(search.trim())}>Record Opt-Out</AppButton>
                  ) : (
                    <AppButton size="sm" onClick={() => recordOptIn(search.trim())}>Record Opt-In</AppButton>
                  )}
                </div>
              )}
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {canManage && (
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Shield size={18} /> Bulk Opt-In</AppCardTitle>
          <p className="settings-card-desc">Opt-in multiple students at once (up to 500)</p>
        </AppCardHeader>
        <AppCardContent>
          <div className="settings-field">
            <label>Student IDs</label>
            <textarea
              rows={3}
              value={bulkIds}
              onChange={e => setBulkIds(e.target.value)}
              placeholder="Paste Student IDs (one per line or comma-separated)..."
              style={{ maxWidth: 500 }}
            />
            <span className="wa-text-muted wa-text-xs">
              Only students with a phone number will be opted in.
            </span>
          </div>

          <Separator />
          <div className="settings-save-row">
            <AppButton size="sm" onClick={handleBulkOptIn} disabled={bulkLoading}>
              {bulkLoading ? "Processing..." : "Bulk Opt-In"}
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>
      )}
    </>
  );
};

export default WhatsAppDashboard;
